<template>
  <div class="page-content">
    <div class="windows-management">
      <div class="page-header">
        <div class="header-left">
          <!-- 页面标题 -->
          <div class="title-section">
            <a 
              href="https://www.yuque.com/liuce1996/yeya/dkc7i6dyclpleyca?singleDoc#" 
              target="_blank" 
              class="update-log-link"
              title="查看花狐脚本更新日志"
            >
              📝 {{ t('window.changelog') }}
            </a>
            <!-- 查看模式提示 -->
            <div v-if="isViewMode" class="view-mode-badge" @click="handleBackUserList">
              <span class="badge-icon">👁️</span>
              <span class="badge-text">{{ t('window.viewingUser', { user: targetUsername }) }}</span>
            </div>
          </div>
          <!-- 统计数字 -->
          <div class="stats-inline">
            <div class="stat-item">
              <span class="stat-value">{{ userWindowLimit }}</span>
              <span class="stat-label">{{ t('window.statLimit') }}</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <span class="stat-value">{{ stats.total_windows || 0 }}</span>
              <span class="stat-label">{{ t('window.statTotal') }}</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <span class="stat-value">{{ stats.online_windows || 0 }}</span>
              <span class="stat-label">{{ t('window.statOnline') }}</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <span class="stat-value">{{ stats.offline_windows || 0 }}</span>
              <span class="stat-label">{{ t('window.statOffline') }}</span>
            </div>
            
            <div class="stat-divider diamond-divider"></div>
            <div class="stat-item diamond-stat">
              <span class="stat-label">{{ t('window.statTotalGold') }}</span>
              <span class="stat-value">{{ formatDiamondCount(computedTotalGold) }}</span>
            </div>
          </div>
        </div>
                  <div class="header-actions">
            <!-- 手动刷新按钮 -->
            <button class="refresh-btn" @click="handleManualRefresh" :disabled="loading || refreshCooldown">
              <svg class="refresh-icon" :class="{ spinning: loading }" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 4V10H7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M23 20V14H17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M3.51 15A9 9 0 0 0 18.36 18.36L23 14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              {{ refreshCooldown ? `${refreshCountdown}s` : t('common.refresh') }}
            </button>
            
            <!-- 网格/列表切换（移动端放在刷新按钮右边） -->
            <div class="mobile-toggle-buttons">
              <button 
                :class="['mobile-toggle-btn', { active: viewMode === 'grid' }]"
                @click="viewMode = 'grid'"
              >
                {{ t('window.grid') }}
              </button>
              <button 
                :class="['mobile-toggle-btn', { active: viewMode === 'list' }]"
                @click="viewMode = 'list'"
              >
                {{ t('window.list') }}
              </button>
            </div>
            
            <!-- 移动端第二行：筛选配置和批量操作 -->
            <div class="mobile-second-row">
              <!-- 筛选配置 -->
              <div class="mobile-filter-select">
                <el-select 
                  v-model="configIdFilter" 
                  :placeholder="t('window.filterConfig')" 
                  @change="handleConfigFilter"
                  clearable
                >
                  <el-option :label="t('window.allConfigs')" :value="null"></el-option>
                  <el-option 
                    v-for="config in availableConfigs" 
                    :key="config.id" 
                    :label="config.name" 
                    :value="config.id"
                  ></el-option>
                </el-select>
              </div>
              
              <!-- 移动端分组筛选 -->
              <div class="mobile-filter-select">
                <el-select 
                  v-model="groupFilter" 
                  :placeholder="t('window.filterGroup')" 
                  @change="handleGroupFilter"
                  clearable
                >
                  <el-option :label="t('common.all')" :value="null"></el-option>
                  <el-option
                    v-for="group in userGroups"
                    :key="group"
                    :label="group || '无分组'"
                    :value="group"
                  />
                  <el-option 
                    label="未分组" 
                    :value="'__no_group__'"
                    disabled
                    class="group-divider-option"
                  ></el-option>
                </el-select>
              </div>
              
              <!-- 批量操作（仅列表模式显示，查看模式下隐藏） -->
              <el-dropdown 
                v-if="viewMode === 'list' && !isViewMode"
                trigger="click" 
                placement="bottom-start"
                @command="handleMobileBatchOperation"
                :disabled="selectedRoles.length === 0"
              >
                <button 
                  class="mobile-batch-btn batch-operation-btn"
                  :disabled="selectedRoles.length === 0"
                  :class="{ disabled: selectedRoles.length === 0 }"
                >
                  批量操作{{ selectedRoles.length > 0 ? ` (${selectedRoles.length})` : '' }}
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" style="margin-left: 4px;">
                    <path d="M7 10l5 5 5-5z"/>
                  </svg>
                </button>
                <template #dropdown>
                  <el-dropdown-menu class="batch-operations-dropdown">
                    <el-dropdown-item command="batch_config">
                      <el-icon><Setting /></el-icon> 更换配置
                    </el-dropdown-item>
                    <el-dropdown-item command="batch_group">
                      <el-icon><Folder /></el-icon> 设置分组
                    </el-dropdown-item>
                    <el-dropdown-item command="batch_ai_answer">
                      <el-icon><EditPen /></el-icon> AI自定义答题
                    </el-dropdown-item>
                    <el-dropdown-item command="batch_auto_answer_on">
                      <el-icon><CircleCheck /></el-icon> 一键开启自动答题
                    </el-dropdown-item>
                    <el-dropdown-item command="batch_auto_answer_off">
                      <el-icon><CircleClose /></el-icon> 一键关闭自动答题
                    </el-dropdown-item>
                    <el-dropdown-item command="batch_delete" style="color: #f56c6c;">
                      <el-icon><Delete /></el-icon> 删除选中
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
            
            <!-- 分页控件（移动端，单独一行，放在筛选配置下面，靠右显示） -->
            <div class="mobile-pagination-row">
              <!-- 只看离线筛选（移动端放在分页组件左侧） -->
              <div class="mobile-offline-filter">
                <el-checkbox v-model="showOfflineOnly" size="small" @change="handleOfflineFilter">
                  {{ t('window.offlineOnly') }}
                </el-checkbox>
              </div>
              
              <div class="custom-pagination mobile-pagination">
                <el-select 
                  v-model="devicePagination.size" 
                  @change="handleDeviceSizeChange"
                  size="small"
                  style="width: 80px;"
                  popper-class="page-size-select-popper mobile-page-size-popper"
                  placement="bottom-start"
                >
                  <el-option :value="20" label="20条/页"></el-option>
                  <el-option :value="50" label="50条/页"></el-option>
                  <el-option :value="100" label="100条/页"></el-option>
                </el-select>
                
                <div class="page-controls">
                  <button 
                    class="page-btn prev-btn" 
                    :disabled="devicePagination.page <= 1"
                    @click="handleDevicePageChange(devicePagination.page - 1)"
                  >
                    ‹
                  </button>
                  
                  <span class="page-info">
                    {{ devicePagination.page }}/{{ Math.ceil(devicePagination.total / devicePagination.size) || 1 }}
                  </span>
                  
                  <button 
                    class="page-btn next-btn" 
                    :disabled="devicePagination.page >= Math.ceil(devicePagination.total / devicePagination.size)"
                    @click="handleDevicePageChange(devicePagination.page + 1)"
                  >
                    ›
                  </button>
                </div>
              </div>
            </div>
            
            <!-- 桌面端批量操作下拉菜单（列表模式下显示，查看模式下隐藏） -->
            <el-dropdown 
              v-if="viewMode === 'list' && !isViewMode"
              trigger="click" 
              placement="bottom-start"
              @command="handleMobileBatchOperation"
              :disabled="selectedRoles.length === 0"
              class="desktop-batch-operations"
            >
              <button 
                class="batch-config-btn desktop-batch-operation-btn"
                :disabled="selectedRoles.length === 0"
                :class="{ disabled: selectedRoles.length === 0 }"
              >
                批量操作{{ selectedRoles.length > 0 ? ` (${selectedRoles.length})` : '' }}
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" style="margin-left: 6px;">
                  <path d="M7 10l5 5 5-5z"/>
                </svg>
              </button>
              <template #dropdown>
                <el-dropdown-menu class="batch-operations-dropdown">
                  <el-dropdown-item command="batch_config" style="width: 100% !important">
                    <el-icon><Setting /></el-icon> 更换配置
                  </el-dropdown-item>
                  <el-dropdown-item command="batch_group" style="width: 100% !important">
                    <el-icon><Folder /></el-icon> 设置分组
                  </el-dropdown-item>
                  <el-dropdown-item command="batch_ai_answer" style="width: 100% !important">
                    <el-icon><EditPen /></el-icon> AI自定义答题
                  </el-dropdown-item>
                  <el-dropdown-item command="batch_auto_answer_on" style="width: 100% !important">
                    <el-icon><CircleCheck /></el-icon> 一键开启自动答题
                  </el-dropdown-item>
                  <el-dropdown-item command="batch_auto_answer_off" style="width: 100% !important">
                    <el-icon><CircleClose /></el-icon> 一键关闭自动答题
                  </el-dropdown-item>
                  <el-dropdown-item command="batch_delete" style="width: 100% !important; color: #f56c6c;">
                    <el-icon><Delete /></el-icon> 删除选中
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
            
            <div class="view-controls-right">
              <!-- 配置名称筛选 -->
              <div class="filter-option">
                <el-select 
                  v-model="configIdFilter" 
                  :placeholder="t('window.filterConfig')" 
                  size="small" 
                  style="width: 100px;"
                  clearable
                  @change="handleConfigFilter"
                >
                  <el-option
                    v-for="config in availableConfigs"
                    :key="config.id"
                    :label="config.name"
                    :value="config.id"
                  />
                </el-select>
              </div>
              
              <!-- 分组筛选 -->
              <div class="filter-option">
                <el-select 
                  v-model="groupFilter" 
                  :placeholder="t('window.filterGroup')" 
                  size="small" 
                  style="width: 120px;"
                  clearable
                  @change="handleGroupFilter"
                >
                  <el-option :label="t('common.all')" :value="null"></el-option>
                  <el-option
                    v-for="group in userGroups"
                    :key="group"
                    :label="group || '无分组'"
                    :value="group"
                  />
                  <el-option 
                    label="未分组" 
                    :value="'__no_group__'"
                    disabled
                    class="group-divider-option"
                  ></el-option>
                </el-select>
              </div>
              
              <!-- 排序选择器 -->
              <div class="filter-option sort-controls">
                <span class="sort-label">排序：</span>
                <el-select 
                  v-model="sortBy" 
                  :placeholder="t('window.sortField')" 
                  size="small" 
                  style="width: 110px;"
                  @change="handleSortChange"
                >
                  <el-option
                    v-for="option in sortOptions"
                    :key="option.value"
                    :label="option.label"
                    :value="option.value"
                  />
                </el-select>
                <el-select 
                  v-model="sortOrder" 
                  size="small" 
                  style="width: 70px;"
                  @change="handleSortChange"
                  :title="sortOrder === 'asc' ? '升序：从小到大排列' : '降序：从大到小排列'"
                >
                  <el-option
                    v-for="option in sortOrderOptions"
                    :key="option.value"
                    :label="option.label"
                    :value="option.value"
                    :title="option.value === 'asc' ? '升序：从小到大排列' : '降序：从大到小排列'"
                  />
                </el-select>
              </div>
              
              <!-- 分页控件（桌面端） -->
              <div class="custom-pagination desktop-pagination">
                <el-select 
                  v-model="devicePagination.size" 
                  @change="handleDeviceSizeChange"
                  size="small"
                  style="width: 90px;"
                  popper-class="page-size-select-popper"
                >
                  <el-option :value="20" label="20条/页"></el-option>
                  <el-option :value="50" label="50条/页"></el-option>
                  <el-option :value="100" label="100条/页"></el-option>
                  <el-option :value="200" label="200条/页"></el-option>
                  <el-option :value="500" label="500条/页"></el-option>
                </el-select>
                
                <div class="page-controls">
                  <button 
                    class="page-btn prev-btn" 
                    :disabled="devicePagination.page <= 1"
                    @click="handleDevicePageChange(devicePagination.page - 1)"
                  >
                    ‹
                  </button>
                  
                  <span class="page-info">
                    {{ devicePagination.page }}/{{ Math.ceil(devicePagination.total / devicePagination.size) || 1 }}
                  </span>
                  
                  <button 
                    class="page-btn next-btn" 
                    :disabled="devicePagination.page >= Math.ceil(devicePagination.total / devicePagination.size)"
                    @click="handleDevicePageChange(devicePagination.page + 1)"
                  >
                    ›
                  </button>
                </div>
                
              </div>
              
              <!-- 只看离线筛选（桌面端） -->
              <div class="filter-option desktop-offline-filter">
                <el-checkbox v-model="showOfflineOnly" size="small" @change="handleOfflineFilter">
                  {{ t('window.offlineOnly') }}
                </el-checkbox>
              </div>
              
              <!-- 显示列按钮（桌面端） -->
              <el-dropdown v-if="viewMode === 'list'" trigger="click" placement="bottom-end" class="desktop-column-selector">
                <button class="column-selector-btn">
                  显示列
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                    <path d="M7 10l5 5 5-5z"/>
                  </svg>
                </button>
                <template #dropdown>
                  <el-dropdown-menu class="column-dropdown">
                    <div class="column-dropdown-header">选择要显示的列</div>
                    <div class="column-options">
                      <div v-for="(column, index) in tableColumns" :key="column.key" class="column-option">
                        <el-checkbox 
                          v-model="column.visible" 
                          :disabled="!column.hideable"
                          @change="updateColumnVisibility"
                        >
                          {{ column.label }}
                        </el-checkbox>
                      </div>
                    </div>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
              
              <!-- 一键导出Excel按钮（桌面端，列表模式） -->
              <button 
                v-if="viewMode === 'list'" 
                class="export-excel-btn"
                @click="exportToExcel"
                style="margin-left: 8px;"
              >
                导出Excel
              </button>
              
              <div class="toggle-buttons desktop-toggle-buttons">
                <button 
                  :class="['toggle-btn', { active: viewMode === 'grid' }]"
                  @click="viewMode = 'grid'"
                >
                  网格
                </button>
                <button 
                  :class="['toggle-btn', { active: viewMode === 'list' }]"
                  @click="viewMode = 'list'"
                >
                  列表
                </button>
              </div>
            </div>
        </div>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
        <div class="loading-spinner">
          <div class="spinner"></div>
          <p>加载中...</p>
        </div>
      </div>

      <!-- 窗口展示 -->
      <div v-else class="windows-container">
        <!-- 网格模式 -->
        <div v-if="viewMode === 'grid'" class="windows-grid" :style="{ '--zoom-level': zoomLevel / 100 }">
          <div v-if="filteredWindowList.length === 0" class="empty-state">
            <div class="empty-icon">📱</div>
            <div class="empty-text">{{ showOfflineOnly ? '暂无离线窗口' : '暂无窗口数据' }}</div>
            <div class="empty-desc">{{ showOfflineOnly ? '当前没有离线状态的窗口' : '等待游戏脚本端连接' }}</div>
          </div>
          <div v-else v-for="role in filteredWindowList" :key="role.id" class="window-card">
            <!-- Improved device preview with better image display -->
            <div class="device-preview">
              <div class="screenshot-container">
                <canvas 
                  class="screenshot"
                  @click="() => openCanvasZoom(role.id)"
                  :key="getImageKey(role)"
                  :ref="el => setCanvasRef(el, role.id)"
                  width="300"
                  height="200"
                ></canvas>
                <div class="screenshot-overlay">
                  <div class="window-info">
                    <div class="window-id-badge">窗口 #{{ role.window_id }}</div>
                  </div>
                  <div class="status-info">
                    <div class="status-badge" :class="getDeviceStatus(role)">
                      {{ getStatusText(role) }}
                    </div>
                  </div>
                </div>
                
                <!-- 窗口操作按钮（查看模式下隐藏） -->
                <div v-if="!isViewMode" class="window-actions">
                  <el-dropdown trigger="click" @command="handleWindowAction">
                    <button class="action-btn">
                      <svg class="gear-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                    <template #dropdown>
                      <el-dropdown-menu>
                        <el-dropdown-item :command="{action: 'screenshot', role: role}" style="width: 100% !important; justify-content: center;">
                          <el-icon><Picture /></el-icon> 截图
                        </el-dropdown-item>
                        <el-dropdown-item :command="{action: 'view_logs', role: role}" style="width: 100% !important; justify-content: center;">
                          <el-icon><Document /></el-icon> 查看日志
                        </el-dropdown-item>
                        <el-dropdown-item :command="{action: 'diamond_trend', role: role}" style="width: 100% !important; justify-content: center;">
                          <el-icon><DataLine /></el-icon> 钻石走势
                        </el-dropdown-item>
                        <el-dropdown-item :command="{action: 'view_status', role: role}" style="width: 100% !important; justify-content: center;">
                          <el-icon><View /></el-icon> 查看状态
                        </el-dropdown-item>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                </div>
              </div>
            </div>

            <!-- Simplified and compact device info card -->
            <div class="device-info-card">
              <!-- 基本信息 -->
              <div class="info-header">
                <div class="device-title">
                  <span class="device-name">{{ role.name || t('window.unknownRole') }}</span>
                  <span class="level-badge">Lv.{{ role.level || 0 }}</span>
                  <span class="update-text">{{ formatRelativeTime(role.update_time) }}</span>
                </div>
                <div class="currency-info">
                  <span class="currency-text">{{ t('common.gold') }}:{{ formatSilver(role.datas?.silver) || '-' }}</span>
                </div>
              </div>

              <!-- Simplified system monitoring -->
              <div class="monitoring-section">
                <!-- CPU和内存在一行 -->
                <div class="monitor-row cpu-memory-row">
                  <div class="monitor-item">
                    <span class="monitor-label">CPU {{ getCpuUsage(role) }}%</span>
                    <div class="mini-progress-bar">
                      <div class="mini-progress-fill cpu" :style="{ width: getCpuUsage(role) + '%' }"></div>
                    </div>
                  </div>
                  
                  <div class="monitor-item">
                    <span class="monitor-label">内存 {{ getMemoryUsage(role) }}%</span>
                    <div class="mini-progress-bar">
                      <div class="mini-progress-fill memory" :style="{ width: getMemoryUsage(role) + '%' }"></div>
                    </div>
                  </div>
                </div>
                
                <div class="details-compact">
                  <span class="detail-text">战力: {{ formatCombatPower(role.combat_power) }}</span>
                  <span class="detail-text" :class="getDatasCountAlertClass('hpPotionCount', role.datas)">血瓶: {{ formatDatasCount(role.datas, '血瓶数量') }}</span>
                  <span class="detail-text" :class="getDatasCountAlertClass('mpPotionCount', role.datas)">蓝瓶: {{ formatDatasCount(role.datas, '蓝瓶数量') }}</span>
                  <span class="detail-text" :class="getDatasCountAlertClass('petSnackCount', role.datas)">宠物零食: {{ formatDatasCount(role.datas, '宠物零食数量') }}</span>
                  <span class="detail-text">卷轴: {{ formatDatasCount(role.datas, '卷轴数量') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Element Plus Table Mode -->
        <div v-else class="windows-list-wrapper">
          <el-table
            :data="filteredWindowList"
            stripe
            border
            style="width: 100%"
            height="100%"
            :default-sort="currentTableSort"
            @selection-change="handleTableSelectionChange"
            @sort-change="handleTableSort"
          >
            <!-- 选择列 -->
            <el-table-column
              v-if="!isViewMode"
              type="selection"
              width="55"
              fixed
            />
            
            <!-- 窗口列 -->
            <el-table-column
              prop="window_id"
              label="🪟窗口"
              width="80"
              fixed
              sortable="custom"
            />
            
            <!-- 动态列 -->
            <el-table-column
              v-for="column in visibleScrollableColumns"
              :key="column.key"
              :prop="column.key"
              :label="column.label"
              :width="parseInt(column.width)"
              :sortable="column.sortable ? 'custom' : false"
            >
              <template #default="{ row }">
                <template v-if="column.key === 'role'">
                  <input
                    v-if="!isViewMode"
                    v-model="row.name"
                    type="text"
                    class="role-name-cell-input"
                    :placeholder="t('window.unknownRole')"
                    @blur="saveRoleName(row)"
                    @keyup.enter="$event.target.blur()"
                  />
                  <span v-else>{{ row.name || '-' }}</span>
                </template>
                <template v-else-if="column.key === 'class'">
                  <input
                    v-if="!isViewMode"
                    :value="getRoleVocationName(row)"
                    type="text"
                    class="role-name-cell-input"
                    placeholder="职业"
                    @input="setRoleVocationName(row, $event.target.value)"
                    @blur="saveRoleVocation(row)"
                    @keyup.enter="$event.target.blur()"
                  />
                  <span v-else>{{ row.datas?.vocation_name || '-' }}</span>
                </template>
                <template v-else-if="column.key === 'ai_answer_custom'">
                  <span
                    class="ai-answer-cell-preview"
                    :title="getRoleAiAnswerCustom(row) || '点击编辑'"
                    @click="openAiAnswerDialog(row)"
                  >
                    {{ getRoleAiAnswerCustom(row) || '编辑' }}
                  </span>
                </template>
                <template v-else-if="column.key === 'auto_answer_enabled'">
                  <el-switch
                    v-if="!isViewMode"
                    :model-value="getRoleAutoAnswerEnabled(row)"
                    @change="(val) => handleAutoAnswerToggle(row, val)"
                  />
                  <span v-else>{{ getRoleAutoAnswerEnabled(row) ? '是' : '否' }}</span>
                </template>
                <template v-else-if="column.key === 'answer_test'">
                  <span class="answer-test-cell-link" @click="openAnswerTestDialog(row)">测试</span>
                </template>
                <template v-else-if="column.key === 'region'">{{ row.region || '-' }}</template>
                <template v-else-if="column.key === 'level'">{{ row.level || '-' }}</template>
                <template v-else-if="column.key === 'combat'">{{ formatCombatPower(row.combat_power) }}</template>
                <template v-else-if="column.key === 'guild'">
                  <span v-if="row.datas?.['公会等级'] && row.datas?.['公会身份']">
                    <span v-if="row.datas['公会身份'] === '会长'" style="background: linear-gradient(to right, #ff6b6b, #ffa500); color: white; padding: 2px 8px; border-radius: 4px; font-weight: bold; font-size: 12px;">
                      {{ row.datas['公会等级'] }}:{{ row.datas['公会身份'] }}
                    </span>
                    <span v-else>{{ row.datas['公会等级'] }}:{{ row.datas['公会身份'] }}</span>
                  </span>
                  <span v-else>-</span>
                </template>
                <template v-else-if="column.key === 'equipment'">
                  <div class="equipment-simple-list">
                    <span :class="['equipment-simple-item', getEquipmentColorClass(row.datas?.女武神卡)]">
                      {{ row.datas?.女武神卡 || '-' }}
                    </span>
                    <span :class="['equipment-simple-item', getEquipmentColorClass(row.datas?.飞鸟)]">
                      {{ row.datas?.飞鸟 || '-' }}
                    </span>
                    <span :class="['equipment-simple-item', getEquipmentColorClass(row.datas?.坐骑)]">
                      {{ row.datas?.坐骑 || '-' }}
                    </span>
                    <span :class="['equipment-simple-item', getEquipmentColorClass(row.datas?.宠物)]">
                      {{ row.datas?.宠物 || '-' }}
                    </span>
                  </div>
                </template>
                <template v-else-if="column.key === 'pending'">
                  <div class="pending-list">
                    <span v-if="row.datas?.伙伴待领 === '待领'" class="pending-item pending-partner">伙伴</span>
                    <span v-if="row.datas?.宠物待领 === '待领'" class="pending-item pending-pet">宠物</span>
                    <span v-if="row.datas?.女武神卡待领 === '待领'" class="pending-item pending-valkyrie">女武神</span>
                    <span v-if="!row.datas?.伙伴待领 && !row.datas?.宠物待领 && !row.datas?.女武神卡待领" class="pending-empty">-</span>
                  </div>
                </template>
                <template v-else-if="column.key === 'worldExchange'">
                  <div class="world-exchange-cell">
                    <span v-if="row.datas?.世界交易所 && row.datas?.世界交易所 !== null && row.datas?.世界交易所 !== ''">{{ row.datas.世界交易所 }}</span>
                    <span v-if="row.datas?.['世界交易所-结算'] && row.datas?.['世界交易所-结算'] !== null && row.datas?.['世界交易所-结算'] !== ''" class="settlement-info">
                      有结算：{{ row.datas?.['世界交易所-结算'] }}
                    </span>
                  </div>
                </template>
                <template v-else-if="column.key === 'normalExchange'">{{ row.datas?.普通交易所 || '-' }}</template>
                <template v-else-if="column.key === 'diamond'">{{ formatDiamond(row.datas?.diamond) || '-' }}</template>
                <template v-else-if="column.key === 'yesterday_diamonds'">{{ formatDiamond(row.yesterday_diamonds) || '-' }}</template>
                <template v-else-if="column.key === 'today_diamonds'">{{ formatDiamond(row.today_diamonds) || '-' }}</template>
                <template v-else-if="column.key === 'gwemix'">{{ row.datas?.gwemix || '-' }}</template>
                <template v-else-if="column.key === 'amber'">{{ formatAmber(row.datas?.amber2) || '-' }}</template>
                <template v-else-if="column.key === 'silver'">{{ formatSilver(row.datas?.silver) || '-' }}</template>
                <template v-else-if="column.key === 'game'">{{ row.game }}</template>
                <template v-else-if="column.key === 'device'">{{ row.device_id || '-' }}</template>
                <template v-else-if="column.key === 'version'">{{ row.version || '-' }}</template>
                <template v-else-if="column.key === 'hpPotionCount'">
                  <span :class="getDatasCountAlertClass('hpPotionCount', row.datas)">{{ formatDatasCount(row.datas, '血瓶数量') }}</span>
                </template>
                <template v-else-if="column.key === 'mpPotionCount'">
                  <span :class="getDatasCountAlertClass('mpPotionCount', row.datas)">{{ formatDatasCount(row.datas, '蓝瓶数量') }}</span>
                </template>
                <template v-else-if="column.key === 'petSnackCount'">
                  <span :class="getDatasCountAlertClass('petSnackCount', row.datas)">{{ formatDatasCount(row.datas, '宠物零食数量') }}</span>
                </template>
                <template v-else-if="column.key === 'scrollCount'">{{ formatDatasCount(row.datas, '卷轴数量') }}</template>
                <template v-else-if="column.key === 'status'">
                  <span :class="['status-badge', getDeviceStatus(row)]">
                    {{ getStatusText(row) }}
                  </span>
                </template>
                <template v-else-if="column.key === 'updateTime'">
                  <span>{{ formatRelativeTime(row.update_time) }}</span>
                </template>
                <template v-else-if="column.key === 'config'">{{ row.config_name || '-' }}</template>
                <template v-else-if="column.key === 'group'">
                  <el-tag v-if="row.group_name" size="small" type="info">{{ row.group_name }}</el-tag>
                  <span v-else style="color: #999;">-</span>
                </template>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>

    <!-- 改进日志模态窗口样式 - 悬浮样式、缩小字体、去掉年月日、日期文字同行、点击空白关闭、美化头部 -->
    <el-dialog
      v-model="logDialogVisible"
      :title="`窗口 #${currentRole?.window_id} 日志查看`"
      :width="isMobile ? '95vw' : '500px'"
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      destroy-on-close
      :modal="true"
      :append-to-body="true"
      :top="isMobile ? '2vh' : '5vh'"
      class="log-dialog-floating"
    >
      <div class="log-dialog-content">
        <!--  美化日志信息头部 -->
        <div class="log-header">
          <div class="log-info">
            <div class="log-badges">
              <span class="log-window-badge">窗口 #{{ currentRole?.window_id }}</span>
              <span class="log-role-badge">{{ currentRole?.name || '未知角色' }}</span>
              <span class="log-device-badge">{{ currentRole?.device_id || '-' }}</span>
            </div>
          </div>
          <div class="log-controls">
            <!-- 时间范围选择器 -->
            <div class="time-range-selector">
              <span class="time-label">时间范围:</span>
              <el-select v-model="selectedTimeRange" @change="loadLogs" size="small">
                <el-option
                  v-for="option in timeRangeOptions"
                  :key="option.value"
                  :label="option.label"
                  :value="option.value"
                />
              </el-select>
            </div>
            <div class="log-stats">
              <span class="log-count">共 {{ logData.total }} 条（最新在前）</span>
            </div>
          </div>
        </div>

        <!-- 日志列表 -->
        <div class="log-list-container">
          <div v-if="logLoading" class="log-loading">
            <div class="loading-spinner">
              <div class="spinner"></div>
              <p>加载中...</p>
            </div>
          </div>
          
          <div v-else-if="logData.logs.length === 0" class="log-empty">
            <div class="empty-icon">📝</div>
            <div class="empty-text">暂无日志</div>
          </div>
          
          <!--  日志列表样式 - 缩小字体、日期和文字同行、去掉年月日 -->
          <div v-else class="log-list">
            <div v-for="(log, index) in logData.logs" :key="index" class="log-item">
              <span class="log-time">{{ formatTime(log.time) }}</span>
              <span class="log-content">{{ log.content }}</span>
            </div>
          </div>
        </div>

        <!-- 智能分页器 -->
        <div v-if="logData.total > 0" class="log-pagination">
          <SmartPagination
            v-model:current-page="currentPage"
            v-model:page-size="pageSize"
            :total="logData.total"
            :page-sizes="isMobile ? [20, 50] : [20, 50, 100, 200, 500]"
            :pager-count="isMobile ? 2 : 7"
            @current-page-change="handleLogPageChange"
            @page-size-change="handleLogSizeChange"
          />
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="logDialogVisible = false" size="small">关闭</el-button>
          <el-button type="primary" @click="loadLogs" :loading="logLoading" size="small">
            刷新日志
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 钻石走势对话框 -->
    <el-dialog
      v-model="diamondTrendDialogVisible"
      :title="`窗口 #${currentDiamondTrendRole?.window_id} 钻石走势`"
      :width="isMobile ? '95vw' : '800px'"
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      destroy-on-close
      :modal="true"
      :append-to-body="true"
      class="diamond-trend-dialog"
    >
      <div class="diamond-trend-content">
        <!-- 时间范围选择 -->
        <div class="trend-controls">
          <el-select v-model="selectedWeeks" @change="loadDiamondTrend" size="small" style="width: 150px;">
            <el-option label="最近1周" :value="1" />
            <el-option label="最近2周" :value="2" />
            <el-option label="最近3周" :value="3" />
            <el-option label="最近4周" :value="4" />
          </el-select>
        </div>

        <!-- 加载状态 -->
        <div v-if="diamondTrendLoading" class="trend-loading">
          <el-skeleton :rows="5" animated />
        </div>

        <!-- 图表容器 -->
        <div v-else-if="diamondTrendData.length > 0" class="trend-chart-container">
          <div ref="diamondTrendChartRef" class="trend-chart"></div>
        </div>

        <!-- 无数据提示 -->
        <div v-else class="trend-no-data">
          <p>暂无钻石走势数据</p>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="diamondTrendDialogVisible = false" size="small">关闭</el-button>
          <el-button type="primary" @click="loadDiamondTrend" :loading="diamondTrendLoading" size="small">
            刷新数据
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 批量更换配置对话框 -->
    <el-dialog
      v-model="batchConfigDialogVisible"
      title="批量更换配置"
      :width="isMobile ? '95vw' : '500px'"
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      destroy-on-close
      :modal="true"
      :append-to-body="true"
      class="batch-config-dialog"
    >
      <div class="batch-config-content">
        <div class="selected-info">
          <p>已选择 <strong>{{ selectedRoles.length }}</strong> 个进行配置更换</p>
          <div class="selected-windows-text">
            {{ formatSelectedWindowsText() }}
          </div>
        </div>
        
        <div class="config-selector">
          <label class="selector-label">选择新配置：</label>
          <el-select 
            v-model="selectedConfigId" 
            :placeholder="t('window.selectConfig')"
            size="default"
            style="width: 100%;"
            :loading="configsLoading"
          >
            <el-option
              v-for="config in availableConfigs"
              :key="config.id"
              :label="config.name"
              :value="config.id"
            >
              <div class="config-option">
                <span class="config-name">{{ config.name }}</span>
                <span class="config-game">{{ config.game }}</span>
              </div>
            </el-option>
          </el-select>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="batchConfigDialogVisible = false" size="default">取消</el-button>
          <el-button 
            type="primary" 
            @click="handleBatchConfigUpdate"
            :loading="batchUpdating"
            :disabled="!selectedConfigId"
            size="default"
          >
            {{ batchUpdating ? '更新中...' : '确定更换' }}
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 批量删除账号对话框 -->
    <el-dialog
      v-model="batchDeleteDialogVisible"
      title="批量删除账号"
      :width="isMobile ? '95vw' : '500px'"
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      destroy-on-close
      :modal="true"
      :append-to-body="true"
      class="batch-delete-dialog"
    >
      <div class="batch-delete-content">
        <div class="warning-info">
          <div class="warning-icon">⚠️</div>
          <div class="warning-text">
            <p><strong>危险操作！</strong></p>
            <p>即将删除 <strong class="danger-text">{{ selectedRoles.length }}</strong> 个账号</p>
            <p class="warning-note">此操作不可撤销，只能删除离线15分钟以上的账号</p>
          </div>
        </div>
        
        <div class="selected-windows-preview">
          <div class="preview-title">将删除的窗口：</div>
          <div class="delete-windows-preview">
            {{ formatSelectedWindowsText() }}
          </div>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="batchDeleteDialogVisible = false" size="default">取消</el-button>
          <el-button 
            type="danger" 
            @click="handleBatchDelete"
            :loading="batchDeleting"
            size="default"
          >
            {{ batchDeleting ? '删除中...' : '确认删除' }}
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 单条 AI 自定义答题 -->
    <el-dialog
      v-model="aiAnswerDialogVisible"
      :title="isViewMode ? 'AI自定义答题' : '编辑 AI 自定义答题'"
      :width="isMobile ? '95vw' : '560px'"
      destroy-on-close
      :append-to-body="true"
      class="ai-answer-edit-dialog"
    >
      <textarea
        v-if="!isViewMode"
        v-model="aiAnswerEditText"
        class="batch-ai-answer-textarea"
        rows="8"
        placeholder="例如：我当前穿戴的武器：xxxx，我当前穿戴的衣服：（请尽量用韩文）。"
      />
      <div v-else class="ai-answer-view-text">{{ aiAnswerEditText || '-' }}</div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="aiAnswerDialogVisible = false" size="default">
            {{ isViewMode ? '关闭' : '取消' }}
          </el-button>
          <el-button
            v-if="!isViewMode"
            type="primary"
            :loading="aiAnswerDialogSaving"
            size="default"
            @click="handleAiAnswerDialogSave"
          >
            {{ aiAnswerDialogSaving ? '保存中...' : '保存' }}
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 批量设置 AI 自定义答题 -->
    <el-dialog
      v-model="batchAiAnswerDialogVisible"
      title="批量设置 AI 自定义答题"
      :width="isMobile ? '95vw' : '560px'"
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      destroy-on-close
      :modal="true"
      :append-to-body="true"
      class="batch-ai-answer-dialog"
    >
      <div class="batch-ai-answer-content">
        <p class="info-text">为选中的 <strong>{{ selectedRoles.length }}</strong> 个窗口设置相同内容</p>
        <textarea
          v-model="batchAiAnswerText"
          class="batch-ai-answer-textarea"
          rows="6"
          placeholder="例如：我当前穿戴的武器：xxxx，我当前穿戴的衣服：（请尽量用韩文）。"
        />
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="batchAiAnswerDialogVisible = false" size="default">取消</el-button>
          <el-button
            type="primary"
            @click="handleBatchAiAnswerUpdate"
            :loading="batchAiAnswerUpdating"
            size="default"
          >
            {{ batchAiAnswerUpdating ? '保存中...' : '确定设置' }}
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 批量设置分组对话框 -->
    <el-dialog
      v-model="batchGroupDialogVisible"
      title="批量设置分组"
      :width="isMobile ? '95vw' : '500px'"
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      destroy-on-close
      :modal="true"
      :append-to-body="true"
      class="batch-group-dialog"
    >
      <div class="batch-group-content">
        <div class="info-text">
          <p>为选中的 <strong>{{ selectedRoles.length }}</strong> 个窗口设置分组</p>
        </div>
        
        <div class="group-selector">
          <label>选择分组：</label>
          <el-select 
            v-model="selectedGroupName" 
            :placeholder="t('window.selectGroup')"
            filterable
            allow-create
            default-first-option
            style="width: 100%;"
          >
            <el-option label="移除分组" :value="null"></el-option>
            <el-option
              v-for="group in userGroups"
              :key="group"
              :label="group"
              :value="group"
            />
          </el-select>
        </div>
        
        <div class="group-management" v-if="userGroups.length > 0">
          <el-divider></el-divider>
          <div class="group-list">
            <div class="group-list-title">我的分组：</div>
            <div class="group-tags">
              <el-tag 
                v-for="group in userGroups" 
                :key="group"
                closable
                @close="handleDeleteGroup(group)"
                style="margin: 4px;"
              >
                {{ group }}
              </el-tag>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="batchGroupDialogVisible = false" size="default">取消</el-button>
          <el-button 
            type="primary" 
            @click="handleBatchGroupUpdate"
            :loading="batchGroupUpdating"
            size="default"
          >
            {{ batchGroupUpdating ? '更新中...' : '确定设置' }}
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 查看状态对话框 -->
    <el-dialog
      v-model="statusDialogVisible"
      :title="`窗口 #${currentStatusRole?.window_id} 状态信息`"
      width="600px"
      :close-on-click-modal="false"
    >
      <div class="status-content">
        <div v-if="currentStatusList.length === 0" class="no-status">
          <p>暂无状态信息</p>
        </div>
        <div v-else class="status-grid">
          <div v-for="status in currentStatusList" :key="status.key" class="status-card">
            <div class="status-card-title">{{ formatStatusLabel(status.key) }}</div>
            <div class="status-card-time">{{ status.value }}</div>
          </div>
        </div>
      </div>
      
      <template #footer>
        <el-button @click="statusDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 答题测试 -->
    <AnswerTestDialog v-model="answerTestDialogVisible" :role="answerTestRole" />

    <!-- 图片放大弹窗 -->
    <div v-if="zoomedImageUrl" class="image-zoom-overlay" @click="closeImageZoom">
      <img :src="zoomedImageUrl" class="zoomed-image" @click.stop />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getUserRoles, getRoleStats, getDevicesInfo, updateRole, createClearMainQuestNotification, batchDeleteRoles, getUserGroups, setRoleGroup, batchSetRoleGroup, batchUpdateAiAnswerCustom, batchUpdateAutoAnswerEnabled, deleteGroup } from '../api/roles'
import { getWindowLogs, getDiamondTrend } from '../api/logs'
import * as echarts from 'echarts'
import { getUserConfigs, batchUpdateRoleConfigs } from '../api/configs'
import { useRoute, useRouter } from 'vue-router'
import SmartPagination from '../components/SmartPagination.vue'
import AnswerTestDialog from '../components/AnswerTestDialog.vue'
import { t } from '@/locales/zh'

export default {
  name: 'WindowSettings',
  components: {
    SmartPagination,
    AnswerTestDialog
  },
      setup() {
        const router = useRouter()
      const route = useRoute()
      // 查看其他用户的窗口（管理员功能）
      const targetUserId = computed(() => {
        const userId = route.query.userId
        return userId ? parseInt(userId) : null
      })
      
      const targetUsername = computed(() => {
        return route.query.username || null
      })
      
      // 判断是否是管理员查看模式（只读模式）
      const isViewMode = computed(() => {
        return !!targetUserId.value
      })
      
      // Cookie工具函数（需要在setup开始就定义）
      const getSimpleCookie = (name) => {
        const nameEQ = name + "="
        const ca = document.cookie.split(';')
        for (let i = 0; i < ca.length; i++) {
          let c = ca[i]
          while (c.charAt(0) === ' ') c = c.substring(1, c.length)
          if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
        }
        return null
      }
      
      // 移动端检测
      const isMobile = ref(window.innerWidth <= 768)
      
      const loading = ref(false)
      // 从cookie加载viewMode，默认grid
      const VIEW_MODE_KEY = 'window-view-mode'
      const savedViewMode = getSimpleCookie(VIEW_MODE_KEY)
      const viewMode = ref(savedViewMode === 'list' || savedViewMode === 'grid' ? savedViewMode : 'grid')
      
      // 监听viewMode变化，保存到cookie
      watch(viewMode, (newValue) => {
        const expires = new Date()
        expires.setTime(expires.getTime() + (30 * 24 * 60 * 60 * 1000))
        document.cookie = `${VIEW_MODE_KEY}=${newValue};expires=${expires.toUTCString()};path=/`
      })
      
      const zoomLevel = ref(100) // 缩放级别，默认100%
      const refreshCooldown = ref(false) // 刷新冷却时间控制
      const refreshCountdown = ref(0) // 刷新倒计时
      let imageRefreshTimers = new Map() // 存储每个图片的刷新定时器
      const zoomedImageUrl = ref('') // 放大图片的URL
      let relativeTimeInterval = null // 相对时间刷新定时器

    // 统计数据
    const stats = ref({
      total_windows: 0,
      online_windows: 0,
      offline_windows: 0,
      total_diamonds: 0,
      today_diamonds: 0,
      yesterday_diamonds: 0
    })

    // 用户窗口上限
    const userWindowLimit = computed(() => {
      try {
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
        return currentUser.number || 0
      } catch (error) {
        console.error('获取用户窗口上限失败:', error)
        return 0
      }
    })

    // 计算当前窗口的总金币数量
    const computedTotalGold = computed(() => {
      if (!windowList.value || windowList.value.length === 0) return 0
      return windowList.value.reduce((total, role) => {
        const silver = role.datas?.silver
        const silverNumber = typeof silver === 'number'
          ? silver
          : parseInt(String(silver || 0).replace(/[^0-9]/g, ''), 10) || 0
        return total + silverNumber
      }, 0)
    })

    // 过滤后的窗口列表（现在由后端处理）
    const filteredWindowList = computed(() => {
      // 如果选择的是"未分组"，需要过滤出没有分组的窗口
      if (groupFilter.value === '__no_group__') {
        return windowList.value.filter(role => !role.group_name || role.group_name === '')
      }
      return windowList.value
    })

    // 窗口列表数据
    const windowList = ref([])
    
    // 分页相关（先用默认值，稍后在getCookie函数定义后再更新）
    const devicePagination = ref({
      page: 1,
      size: 100,
      total: 0
    })
    const configIdFilter = ref('')
    const groupFilter = ref(null)
    
    // 分组相关
    const userGroups = ref([])
    const batchGroupDialogVisible = ref(false)
    const selectedGroupName = ref(null)
    const batchGroupUpdating = ref(false)

    const batchAiAnswerDialogVisible = ref(false)
    const batchAiAnswerText = ref('')
    const batchAiAnswerUpdating = ref(false)

    const aiAnswerDialogVisible = ref(false)
    const aiAnswerEditingRow = ref(null)
    const aiAnswerEditText = ref('')
    const aiAnswerDialogSaving = ref(false)

    const answerTestDialogVisible = ref(false)
    const answerTestRole = ref(null)
    
    // 过滤选项
    const showOfflineOnly = ref(false)
    
    // 排序选项
    const sortBy = ref('window_id')
    const sortOrder = ref('asc')

    const sortOrderOptions = computed(() => [
      { value: 'asc', label: t('window.sort.asc') },
      { value: 'desc', label: t('window.sort.desc') }
    ])
    
    // OSS预签名URL自动更新，不再需要手动时间戳

    // 批量选择相关状态
    const selectedRoles = ref([])
    const selectAll = ref(false)
    const indeterminate = computed(() => {
      return selectedRoles.value.length > 0 && selectedRoles.value.length < filteredWindowList.value.length
    })

    // 批量更换配置相关状态
    const batchConfigDialogVisible = ref(false)
    const selectedConfigId = ref('')
    const availableConfigs = ref([])
    const configsLoading = ref(false)
    const batchUpdating = ref(false)

    // 批量删除相关状态
    const batchDeleteDialogVisible = ref(false)
    const batchDeleting = ref(false)
    
    // 批量清空主线相关状态

    // 状态查看相关状态
    const statusDialogVisible = ref(false)
    const currentStatusRole = ref(null)
    const currentStatusList = ref([])

    // 加载数据
    const loadData = async () => {
      loading.value = true
      try {
        // 构建设备查询参数
        const deviceParams = {
          page: devicePagination.value.page,
          size: devicePagination.value.size
        }
        
        // 如果是查看其他用户的窗口（管理员功能），添加userId参数
        if (targetUserId.value) {
          deviceParams.user_id = targetUserId.value
        }
        
        // 如果有配置ID筛选，添加到参数中
        if (configIdFilter.value) {
          deviceParams.config_id = configIdFilter.value
        }
        
        // 如果有分组筛选，添加到参数中
        if (groupFilter.value && groupFilter.value !== '__no_group__') {
          deviceParams.group = groupFilter.value
        }
        
        // 如果只看离线，添加到参数中
        if (showOfflineOnly.value) {
          deviceParams.offline_only = true
        }
        
        // 添加排序参数
        deviceParams.sort_by = sortBy.value
        deviceParams.sort_order = sortOrder.value
        
        // 并行加载统计信息和设备信息（使用新的增强接口）
        const [statsResponse, devicesResponse] = await Promise.all([
          getRoleStats(targetUserId.value),
          getDevicesInfo(deviceParams)
        ])

        stats.value = statsResponse.data || stats.value
        
        // 处理分页响应
        if (devicesResponse.data && typeof devicesResponse.data === 'object' && devicesResponse.data.items) {
          // 分页响应格式
          windowList.value = devicesResponse.data.items || []
          devicePagination.value.total = devicesResponse.data.total || 0
        } else {
          // 兼容旧的非分页响应格式
          windowList.value = devicesResponse.data || []
          devicePagination.value.total = windowList.value.length
        }

        const nextRoleNameMap = new Map()
        const nextVocationNameMap = new Map()
        windowList.value.forEach((row) => {
          if (row?.id != null) {
            nextRoleNameMap.set(row.id, row.name == null ? '' : String(row.name).trim())
            const vocation = row.datas?.vocation_name
            nextVocationNameMap.set(row.id, vocation == null ? '' : String(vocation).trim())
          }
        })
        roleNameLastSaved.value = nextRoleNameMap
        vocationNameLastSaved.value = nextVocationNameMap

        // OSS预签名URL会在每次API调用时自动更新，无需手动管理时间戳
        
        // 更新设备截图
        updateDeviceScreenshots()
        
        // 数据加载完成后，启动图片自动刷新
        startImageAutoRefresh()
        
      } catch (error) {
        console.error('=== 错误捕获开始 ===')
        console.error('错误对象:', error)
        console.error('错误类型:', typeof error)
        console.error('错误消息:', error.message)
        console.error('错误响应:', error.response)
        console.error('错误响应数据:', error.response?.data)
        console.error('错误响应状态:', error.response?.status)
        console.error('=== 错误捕获结束 ===')
        
        let errorMessage = '加载数据失败'
        
        // 处理JWT相关错误
        if (error.response?.data?.message?.includes('JWT signature does not match') ||
            error.response?.data?.message?.includes('JWT validity cannot be asserted') ||
            error.response?.data?.message?.includes('Token已过期')) {
          console.warn('JWT验证失败，清除登录状态并跳转到登录页')
          localStorage.removeItem('token')
          localStorage.removeItem('user')
          ElMessage.error('登录已过期，请重新登录')
          setTimeout(() => {
            window.location.href = '#/login'
          }, 1500)
          return
        }
        
        if (error.response?.status === 401) {
          errorMessage = '登录已过期，请重新登录'
          setTimeout(() => {
            window.location.href = '#/login'
          }, 1500)
        } else if (error.response?.data?.error || error.response?.data?.message) {
          errorMessage = error.response.data.error || error.response.data.message
        }
        
        ElMessage.error(errorMessage)
      } finally {
        loading.value = false
      }
    }

    // 缓存本地IP状态，避免重复检查 - 永久缓存，只检查一次
    const localStatusCache = new Map()

    // 创建隐藏iframe代理，用于隐藏HTTP请求
    let hiddenProxyFrame = null
    
    const createHiddenProxy = () => {
      if (!hiddenProxyFrame) {
        const iframe = document.createElement('iframe')
        iframe.style.display = 'none'
        iframe.style.position = 'absolute'
        iframe.style.left = '-9999px'
        iframe.style.width = '1px'
        iframe.style.height = '1px'
        document.body.appendChild(iframe)
        
        const doc = iframe.contentDocument || iframe.contentWindow.document
        doc.open()
        
        // 构建iframe内容，避免Vue模板解析冲突
        const openTag = '<'
        const closeTag = '>'
        const scriptStart = openTag + 'script' + closeTag
        const scriptEnd = openTag + '/script' + closeTag
        const bodyEnd = openTag + '/body' + closeTag + openTag + '/html' + closeTag
        
        const iframeHTML = '<!DOCTYPE html>' +
          openTag + 'html' + closeTag + openTag + 'head' + closeTag + openTag + '/head' + closeTag + openTag + 'body' + closeTag +
          scriptStart +
          'window.proxyFetch = async function(url, options) {' +
          '  try {' +
          '    const response = await fetch(url, options);' +
          '    return {' +
          '      ok: response.ok,' +
          '      status: response.status,' +
          '      data: response.ok ? await response.json() : null' +
          '    };' +
          '  } catch (error) {' +
          '    throw error;' +
          '  }' +
          '};' +
          'window.proxyImageFetch = async function(url, options) {' +
          '  try {' +
          '    const response = await fetch(url, options);' +
          '    if (response.ok) {' +
          '      const contentType = response.headers.get("content-type");' +
          '      if (contentType && contentType.includes("application/json")) {' +
          '        const data = await response.json();' +
          '        return {' +
          '          ok: response.ok,' +
          '          status: response.status,' +
          '          data: data,' +
          '          blob: null' +
          '        };' +
          '      } else {' +
          '        const blob = await response.blob();' +
          '        return {' +
          '          ok: response.ok,' +
          '          status: response.status,' +
          '          data: null,' +
          '          blob: blob' +
          '        };' +
          '      }' +
          '    }' +
          '    return {' +
          '      ok: response.ok,' +
          '      status: response.status,' +
          '      data: null,' +
          '      blob: null' +
          '    };' +
          '  } catch (error) {' +
          '    throw error;' +
          '  }' +
          '};' +
          scriptEnd +
          bodyEnd
        
        doc.write(iframeHTML)
        doc.close()
        hiddenProxyFrame = iframe
      }
      return hiddenProxyFrame
    }

    // 隐藏HTTP请求函数
    const sendHiddenRequest = async (url, options = {}) => {
      const proxy = createHiddenProxy()
      const proxyWindow = proxy.contentWindow
      
      // 确保代理函数已加载
      let attempts = 0
      while (!proxyWindow.proxyFetch && attempts < 10) {
        await new Promise(resolve => setTimeout(resolve, 50))
        attempts++
      }
      
      if (!proxyWindow.proxyFetch) {
        throw new Error('代理初始化失败')
      }
      
      return await proxyWindow.proxyFetch(url, options)
    }

    // 隐藏图片请求函数
    const getHiddenImage = async (url, options = {}) => {
      const proxy = createHiddenProxy()
      const proxyWindow = proxy.contentWindow
      
      // 确保代理函数已加载
      let attempts = 0
      while (!proxyWindow.proxyImageFetch && attempts < 10) {
        await new Promise(resolve => setTimeout(resolve, 50))
        attempts++
      }
      
      if (!proxyWindow.proxyImageFetch) {
        throw new Error('图片代理初始化失败')
      }
      
      return await proxyWindow.proxyImageFetch(url, options)
    }

    // 检查本地IP状态 - 只检查一次，永久缓存结果
    const checkLocalStatus = async (ip) => {
      if (!ip) return false
      
      // 如果已经检查过，直接返回缓存结果
      if (localStatusCache.has(ip)) {
        const cached = localStatusCache.get(ip)
        console.log(`设备IP ${ip} 使用缓存状态: ${cached}`)
        return cached
      }
      
      // 直接返回false，不再发送请求
      localStatusCache.set(ip, false)
      return false
    }

    // 获取本地截图
    const getLocalScreenshot = async (ip) => {
      if (!ip) return null
      
      try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 2000) // 2秒超时，快速失败
        
        const response = await getHiddenImage(`http://${ip}:12881/screenshot`, {
          method: 'GET',
          signal: controller.signal
        })
        
        clearTimeout(timeoutId)
        
        if (response.ok && response.data && response.data.screenshot) {
          return `data:image/jpeg;base64,${response.data.screenshot}`
        }
      } catch (error) {
        // 连接失败是正常情况，静默处理
        // 不输出警告日志，避免控制台日志过多
      }
      
      return null
    }

    // 缓存本地截图，避免频繁请求
    const localScreenshotCache = new Map()
    const SCREENSHOT_CACHE_DURATION = 15000 // 15秒缓存时间，减少请求频率

    // 响应式截图URL存储
    const deviceScreenshotUrls = ref(new Map())
    
    // Canvas引用存储
    const canvasRefs = ref(new Map())
    
    // 清理MJPEG流
    const cleanupMjpegStreams = (canvas) => {
      if (canvas && canvas._mjpegStreams) {
        canvas._mjpegStreams.forEach(stream => {
          stream.stop()
        })
        canvas._mjpegStreams.clear()
      }
    }
    
    // 设置canvas引用
    const setCanvasRef = (el, deviceId) => {
      if (el) {
        // 清理之前的流
        cleanupMjpegStreams(el)
        
        canvasRefs.value.set(deviceId, el)
        // 立即绘制图片
        drawImageToCanvas(el, deviceId)
      } else {
        // 清理流并删除引用
        const existingCanvas = canvasRefs.value.get(deviceId)
        if (existingCanvas) {
          cleanupMjpegStreams(existingCanvas)
        }
        canvasRefs.value.delete(deviceId)
      }
    }
    
    // 绘制图片到canvas
    const drawImageToCanvas = async (canvas, deviceId) => {
      if (!canvas) return
      
      const ctx = canvas.getContext('2d')
      const device = windowList.value.find(d => d.id === deviceId)
      
      if (!device) return
      
      try {
        // 获取图片URL
        const imageUrl = await getScreenshotUrlAsync(device)
        console.log(`设备 ${device.id} 获取到图片URL:`, imageUrl)
        
        if (!imageUrl) {
          // 根据设备情况显示不同的错误信息
          const localIp = device.datas?.ip || device.ip
          let errorText = '无截图'
          
          if (localIp) {
            // 有本地IP但无法获取截图
            errorText = '图片加载失败'
          } else {
            // 没有本地IP也没有远程截图
            errorText = '暂无截图'
          }
          
          drawPlaceholder(ctx, canvas.width, canvas.height, errorText)
          return
        }
        
        // 检查是否为视频流
        if (imageUrl.includes('/stream')) {
          console.log(`设备 ${device.id} 使用实时视频流`)
          drawVideoStream(ctx, canvas, imageUrl, device.id)
        } else {
          // 创建图片对象
          const img = new Image()
          // 对于OSS预签名URL，可能不需要设置crossOrigin
          // img.crossOrigin = 'anonymous'
          
          img.onload = () => {
            console.log(`设备 ${device.id} 图片加载成功，尺寸: ${img.width}x${img.height}`)
            drawImageOnCanvas(ctx, img, canvas, device.id)
          }
          
          img.onerror = (error) => {
            console.warn(`设备 ${device.id} 图片加载失败，尝试使用保底截图:`, imageUrl, error)
            
            // 图片加载失败时，尝试使用设备的远程截图作为保底
            if (device.screenshot_url && imageUrl !== device.screenshot_url) {
              console.log(`设备 ${device.id} 使用远程截图作为保底:`, device.screenshot_url)
              
              // 创建保底图片
              const fallbackImg = new Image()
              fallbackImg.onload = () => {
                console.log(`设备 ${device.id} 保底截图加载成功`)
                
                // 清除canvas
                ctx.clearRect(0, 0, canvas.width, canvas.height)
                
                // 计算缩放比例
                const canvasRatio = canvas.width / canvas.height
                const imgRatio = fallbackImg.width / fallbackImg.height
                
                let drawWidth, drawHeight, drawX, drawY
                
                if (imgRatio > canvasRatio) {
                  // 图片更宽（横屏），写死尺寸
                  drawWidth = 300
                  drawHeight = 200
                  drawX = 0
                  drawY = (canvas.height - drawHeight) / 2
                } else {
                  // 图片更高，以高度为准
                  drawHeight = canvas.height
                  drawWidth = canvas.height * imgRatio
                  drawX = (canvas.width - drawWidth) / 2
                  drawY = 0
                }
                
                // 绘制保底截图
                ctx.drawImage(fallbackImg, drawX, drawY, drawWidth, drawHeight)
                
                // 添加保底标识
                ctx.fillStyle = 'rgba(255, 165, 0, 0.8)'
                ctx.fillRect(5, 5, 60, 20)
                ctx.fillStyle = 'white'
                ctx.font = '12px Arial'
                ctx.fillText('保底截图', 8, 18)
              }
              
              fallbackImg.onerror = () => {
                console.warn(`设备 ${device.id} 保底截图也加载失败`)
                drawPlaceholder(ctx, canvas.width, canvas.height, '图片加载失败')
              }
              
              fallbackImg.src = device.screenshot_url
            } else {
              drawPlaceholder(ctx, canvas.width, canvas.height, '图片加载失败')
            }
          }
          
          img.src = imageUrl
        }
        
      } catch (error) {
        // 加载过程中出错
        const localIp = device.datas?.ip || device.ip
        const errorText = localIp ? '连接失败' : '加载中...'
        drawPlaceholder(ctx, canvas.width, canvas.height, errorText)
      }
    }
    
    // 绘制占位符
    const drawPlaceholder = (ctx, width, height, text = '无截图') => {
      // 清除canvas
      ctx.clearRect(0, 0, width, height)
      
      // 绘制背景
      ctx.fillStyle = '#f3f4f6'
      ctx.fillRect(0, 0, width, height)
      
      // 绘制文字
      ctx.fillStyle = '#9ca3af'
      ctx.font = '14px Arial'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(text, width / 2, height / 2)
    }
    
    // 绘制图片到canvas
    const drawImageOnCanvas = (ctx, img, canvas, deviceId) => {
      // 清除canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      
      // 计算缩放比例，保持宽高比
      const canvasRatio = canvas.width / canvas.height
      const imgRatio = img.width / img.height
      
      console.log(`设备 ${deviceId} Canvas尺寸: ${canvas.width}x${canvas.height}, Canvas比例: ${canvasRatio.toFixed(2)}`)
      console.log(`设备 ${deviceId} 图片比例: ${imgRatio.toFixed(2)}`)
      
      let drawWidth, drawHeight, drawX, drawY
      
      if (imgRatio > canvasRatio) {
        // 图片更宽（横屏），写死尺寸
        drawWidth = 300
        drawHeight = 200
        drawX = 0
        drawY = (canvas.height - drawHeight) / 2
        console.log(`设备 ${deviceId} 图片更宽（横屏），使用固定尺寸: 300x200`)
      } else {
        // 图片更高，以高度为准
        drawHeight = canvas.height
        drawWidth = canvas.height * imgRatio
        drawX = (canvas.width - drawWidth) / 2
        drawY = 0
        console.log(`设备 ${deviceId} 图片更高，使用高度适配`)
      }
      
      console.log(`设备 ${deviceId} 绘制参数: 位置(${drawX.toFixed(1)}, ${drawY.toFixed(1)}), 尺寸: ${drawWidth.toFixed(1)}x${drawHeight.toFixed(1)}`)
      
      // 绘制图片
      ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight)
      
      console.log(`设备 ${deviceId} 图片绘制完成`)
    }
    
    // 绘制MJPEG实时视频流
    const drawVideoStream = (ctx, canvas, streamUrl, deviceId) => {
      console.log(`设备 ${deviceId} 开始加载MJPEG视频流:`, streamUrl)
      
      let isStreamActive = true
      let animationId = null
      let lastFrameTime = Date.now()
      let fallbackCheckTimer = null
      
      // 创建隐藏的img元素来接收MJPEG流
      const streamImg = document.createElement('img')
      streamImg.style.position = 'absolute'
      streamImg.style.left = '-9999px'
      streamImg.style.top = '-9999px'
      streamImg.style.width = '1px'
      streamImg.style.height = '1px'
      streamImg.crossOrigin = 'anonymous'
      document.body.appendChild(streamImg)
      
      console.log(`设备 ${deviceId} 创建隐藏img元素用于MJPEG流`)
      
      // 持续绘制函数
      const continuousDraw = () => {
        if (!isStreamActive) {
          if (animationId) {
            clearTimeout(animationId)
          }
          return
        }
        
        // 检查img是否有内容
        if (streamImg.naturalWidth > 0 && streamImg.naturalHeight > 0) {
          // 清除canvas
          ctx.clearRect(0, 0, canvas.width, canvas.height)
          
          // 计算缩放比例
          const canvasRatio = canvas.width / canvas.height
          const imgRatio = streamImg.naturalWidth / streamImg.naturalHeight
          
          let drawWidth, drawHeight, drawX, drawY
          
          if (imgRatio > canvasRatio) {
            // 图片更宽（横屏），写死尺寸
            drawWidth = 300
            drawHeight = 200
            drawX = 0
            drawY = (canvas.height - drawHeight) / 2
          } else {
            // 图片更高，以高度为准
            drawHeight = canvas.height
            drawWidth = canvas.height * imgRatio
            drawX = (canvas.width - drawWidth) / 2
            drawY = 0
          }
          
          // 绘制当前帧到canvas
          try {
            ctx.drawImage(streamImg, drawX, drawY, drawWidth, drawHeight)
            lastFrameTime = Date.now() // 更新最后帧时间
            // 减少日志频率，避免刷屏
            if (Math.random() < 0.1) { // 10%概率输出日志
              console.log(`设备 ${deviceId} MJPEG流正在实时绘制`)
            }
          } catch (error) {
            console.warn(`设备 ${deviceId} 绘制MJPEG帧失败:`, error)
          }
        } else {
          // 显示加载状态
          drawPlaceholder(ctx, canvas.width, canvas.height, '连接MJPEG流中...')
        }
        
        // 匹配5fps的流帧率，每200ms刷新一次
        animationId = setTimeout(() => {
          continuousDraw()
        }, 200) // 200ms = 5fps
      }
      
      // 开始绘制循环
      continuousDraw()
      
      // 启动保底检查定时器，每30秒检查一次MJPEG流是否正常
      fallbackCheckTimer = setInterval(() => {
        const now = Date.now()
        const timeSinceLastFrame = now - lastFrameTime
        
        // 如果超过30秒没有新帧，切换到保底截图
        if (timeSinceLastFrame > 30000 && isStreamActive) {
          console.warn(`设备 ${deviceId} MJPEG流超过30秒无更新，切换到保底截图`)
          
          const device = windowList.value.find(d => d.id === deviceId)
          if (device && device.screenshot_url) {
            const fallbackImg = new Image()
            fallbackImg.onload = () => {
              console.log(`设备 ${deviceId} 保底截图加载成功`)
              
              // 清除canvas
              ctx.clearRect(0, 0, canvas.width, canvas.height)
              
              // 计算缩放比例
              const canvasRatio = canvas.width / canvas.height
              const imgRatio = fallbackImg.naturalWidth / fallbackImg.naturalHeight
              
              let drawWidth, drawHeight, drawX, drawY
              
              if (imgRatio > canvasRatio) {
                drawWidth = 300
                drawHeight = 200
                drawX = 0
                drawY = (canvas.height - drawHeight) / 2
              } else {
                drawHeight = canvas.height
                drawWidth = canvas.height * imgRatio
                drawX = (canvas.width - drawWidth) / 2
                drawY = 0
              }
              
              // 绘制保底截图
              ctx.drawImage(fallbackImg, drawX, drawY, drawWidth, drawHeight)
              
              // 添加保底标识
              ctx.fillStyle = 'rgba(255, 165, 0, 0.8)'
              ctx.fillRect(5, 5, 80, 20)
              ctx.fillStyle = 'white'
              ctx.font = '12px Arial'
              ctx.fillText('流断线保底', 8, 18)
            }
            
            fallbackImg.onerror = () => {
              drawPlaceholder(ctx, canvas.width, canvas.height, '流已断线')
            }
            
            fallbackImg.src = device.screenshot_url
          }
        }
      }, 30000) // 每30秒检查一次
      
      // 设置MJPEG流源
      streamImg.onload = () => {
        console.log(`设备 ${deviceId} MJPEG流连接成功，尺寸: ${streamImg.naturalWidth}x${streamImg.naturalHeight}`)
      }
      
      streamImg.onerror = (error) => {
        console.warn(`设备 ${deviceId} MJPEG流连接失败，尝试使用远程截图:`, error)
        
        // MJPEG流失败时，尝试使用远程截图作为保底
        const device = windowList.value.find(d => d.id === deviceId)
        if (device && device.screenshot_url) {
          console.log(`设备 ${deviceId} 使用远程截图作为保底:`, device.screenshot_url)
          
          // 创建新的img来加载远程截图
          const fallbackImg = new Image()
          fallbackImg.onload = () => {
            console.log(`设备 ${deviceId} 远程截图加载成功，作为保底显示`)
            
            // 清除canvas
            ctx.clearRect(0, 0, canvas.width, canvas.height)
            
            // 计算缩放比例
            const canvasRatio = canvas.width / canvas.height
            const imgRatio = fallbackImg.naturalWidth / fallbackImg.naturalHeight
            
            let drawWidth, drawHeight, drawX, drawY
            
            if (imgRatio > canvasRatio) {
              // 图片更宽（横屏），写死尺寸
              drawWidth = 300
              drawHeight = 200
              drawX = 0
              drawY = (canvas.height - drawHeight) / 2
            } else {
              // 图片更高，以高度为准
              drawHeight = canvas.height
              drawWidth = canvas.height * imgRatio
              drawX = (canvas.width - drawWidth) / 2
              drawY = 0
            }
            
            // 绘制保底截图
            ctx.drawImage(fallbackImg, drawX, drawY, drawWidth, drawHeight)
            
            // 添加保底标识
            ctx.fillStyle = 'rgba(255, 165, 0, 0.8)'
            ctx.fillRect(5, 5, 60, 20)
            ctx.fillStyle = 'white'
            ctx.font = '12px Arial'
            ctx.fillText('保底截图', 8, 18)
          }
          
          fallbackImg.onerror = () => {
            console.warn(`设备 ${deviceId} 远程截图也加载失败`)
            drawPlaceholder(ctx, canvas.width, canvas.height, '图片加载失败')
          }
          
          fallbackImg.src = device.screenshot_url
        } else {
          drawPlaceholder(ctx, canvas.width, canvas.height, 'MJPEG流连接失败')
        }
      }
      
      streamImg.src = streamUrl
      
      // 存储流状态，用于清理
      const canvas_element = canvas
      if (!canvas_element._mjpegStreams) {
        canvas_element._mjpegStreams = new Set()
      }
      canvas_element._mjpegStreams.add({
        img: streamImg,
        stop: () => {
          console.log(`设备 ${deviceId} 停止MJPEG流并清理资源`)
          isStreamActive = false
          if (animationId) {
            clearTimeout(animationId)
          }
          if (fallbackCheckTimer) {
            clearInterval(fallbackCheckTimer)
          }
          if (streamImg.parentNode) {
            streamImg.parentNode.removeChild(streamImg)
          }
        }
      })
    }

    // 获取设备截图URL（异步）
    const getScreenshotUrlAsync = async (device) => {
      // 检查是否有本地IP信息
      const localIp = device.datas?.ip || device.ip
      
      if (localIp) {
        // 检查本地服务状态
        const isLocalActive = await checkLocalStatus(localIp)
        
        if (isLocalActive) {
          // 本地服务可用(status=1)，使用HTTPS实时视频流
          const streamUrl = `https://${localIp}:12882/stream`
          console.log(`设备 ${device.id} 本地服务可用(status=1)，使用HTTPS视频流:`, streamUrl)
          return streamUrl
        }
        
        // 本地服务不可用(status不为1)或获取截图失败
        // 仍然尝试使用远程截图，如果没有则显示错误信息
      if (device.screenshot_url) {
          console.log(`设备 ${device.id} 本地服务不可用，使用远程截图:`, device.screenshot_url)
        return device.screenshot_url
      }
      
        // 如果连远程截图都没有，返回null让canvas显示"图片加载失败"
        return null
      }
      
      // 没有本地IP，使用远程截图URL
      if (device.screenshot_url) {
        return device.screenshot_url
      }
      
      // 备用方案 - 返回null让canvas显示占位符
      return null
    }

    // 同步获取设备截图URL（用于模板）
    const getScreenshotUrl = (device) => {
      const deviceId = device.id
      const cached = deviceScreenshotUrls.value.get(deviceId)
      
      if (cached) {
        return cached
      }
      
      // 如果没有缓存，先返回默认值，然后异步加载
      const defaultUrl = device.screenshot_url || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk0YTNiOCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPuaXoOaIquWbvjwvdGV4dD48L3N2Zz4='
      
      // 异步加载真正的截图URL
      getScreenshotUrlAsync(device).then(url => {
        if (url) {
          const newUrls = new Map(deviceScreenshotUrls.value)
          newUrls.set(deviceId, url)
          deviceScreenshotUrls.value = newUrls
        }
      }).catch(error => {
        // 静默处理加载失败，避免控制台警告
      })
      
      return defaultUrl
    }

    // 更新设备截图URL的方法
    const updateDeviceScreenshots = async () => {
      if (!windowList.value || windowList.value.length === 0) return
      
      const promises = windowList.value.map(async (device) => {
        try {
          const url = await getScreenshotUrlAsync(device)
          
          // 如果有对应的canvas，重新绘制
          const canvas = canvasRefs.value.get(device.id)
          if (canvas) {
            drawImageToCanvas(canvas, device.id)
          }
          
          return { deviceId: device.id, url }
        } catch (error) {
          // 静默处理更新失败，返回默认截图URL
          return { deviceId: device.id, url: device.screenshot_url }
        }
      })
      
      const results = await Promise.all(promises)
      const newUrls = new Map(deviceScreenshotUrls.value)
      
      results.forEach(({ deviceId, url }) => {
        if (url) {
          newUrls.set(deviceId, url)
        }
      })
      
      deviceScreenshotUrls.value = newUrls
    }

    // 获取图片的唯一key，用于强制Vue重新渲染图片
    const getImageKey = (device) => {
      // 使用设备ID和截图URL的哈希作为key，当URL变化时自动更新
      if (device.screenshot_url) {
        // 简单哈希函数
        let hash = 0
        for (let i = 0; i < device.screenshot_url.length; i++) {
          const char = device.screenshot_url.charCodeAt(i)
          hash = ((hash << 5) - hash) + char
          hash = hash & hash // 转换为32位整数
        }
        return `${device.id}-${Math.abs(hash)}`
      }
      return `${device.id}-default`
    }

    // 判断是否为横屏
    const isLandscape = (role) => {
      // 可以根据websocket数据或其他字段判断屏幕方向
      // 这里先默认为竖屏，可以根据实际需求调整
      return false
    }

    // 获取设备状态
    const getDeviceStatus = (device) => {
      // 后端已经处理好设备状态
      return device.device_status || 'offline'
    }

    const getStatusText = (device) => {
      // 后端已经处理好状态文本
      return device.status_text || '未知'
    }

    // 图片放大功能
    const openImageZoom = (imageUrl) => {
      zoomedImageUrl.value = imageUrl
    }
    
    // Canvas点击放大功能
    const openCanvasZoom = async (deviceId) => {
      const device = windowList.value.find(d => d.id === deviceId)
      if (device) {
        const imageUrl = await getScreenshotUrlAsync(device)
        if (imageUrl) {
          zoomedImageUrl.value = imageUrl
        }
      }
    }

    const closeImageZoom = () => {
      zoomedImageUrl.value = ''
    }

    // 获取CPU使用率
    const getCpuUsage = (device) => {
      return device.datas?.cpu_usage || device.cpu_usage || 0
    }

    // 获取内存使用率
    const getMemoryUsage = (device) => {
      return device.datas?.memory_usage || device.memory_usage || 0
    }

    // 获取装备颜色类
    const getEquipmentColorClass = (color) => {
      if (!color) return 'equipment-gray'
      
      const colorMap = {
        '灰': 'equipment-gray',
        '蓝': 'equipment-blue', 
        '绿': 'equipment-green',
        '红': 'equipment-red',
        '金': 'equipment-gold'
      }
      
      return colorMap[color] || 'equipment-gray'
    }

    const HP_POTION_LOW_THRESHOLD = 200
    const MP_POTION_LOW_THRESHOLD = 500
    const PET_SNACK_LOW_THRESHOLD = 10

    const parseDatasCount = (datas, field) => {
      if (!datas || datas[field] == null || datas[field] === '') return null
      const n = Number(String(datas[field]).replace(/,/g, '').trim())
      return Number.isFinite(n) ? n : null
    }

    const formatDatasCount = (datas, field) => {
      const count = parseDatasCount(datas, field)
      if (count == null) return '-'
      return count
    }

    const getDatasCountAlertClass = (columnKey, datas) => {
      const rules = {
        hpPotionCount: { field: '血瓶数量', threshold: HP_POTION_LOW_THRESHOLD, className: 'count-alert-hp' },
        mpPotionCount: { field: '蓝瓶数量', threshold: MP_POTION_LOW_THRESHOLD, className: 'count-alert-mp' },
        petSnackCount: { field: '宠物零食数量', threshold: PET_SNACK_LOW_THRESHOLD, className: 'count-alert-pet' }
      }
      const rule = rules[columnKey]
      if (!rule) return ''
      const count = parseDatasCount(datas, rule.field)
      if (count == null || count >= rule.threshold) return ''
      return rule.className
    }

    // 查看状态
    const handleViewStatus = (role) => {
      currentStatusRole.value = role
      
      // 提取所有status_开头的字段
      const statusList = []
      if (role.datas) {
        Object.keys(role.datas).forEach(key => {
          if (key.startsWith('status_') && role.datas[key]) {
            statusList.push({
              key: key,
              value: formatStatusValue(role.datas[key])
            })
          }
        })
      }
      
      currentStatusList.value = statusList
      statusDialogVisible.value = true
    }

    // 格式化状态标签
    const formatStatusLabel = (key) => {
      return key.replace('status_', '').replace(/_/g, ' ')
    }

    // 格式化状态值（时间戳转换为时间格式）
    const formatStatusValue = (value) => {
      // 检查是否为时间戳（10位或13位数字）
      if (/^\d{10}$/.test(value)) {
        // 10位时间戳，秒级
        const date = new Date(parseInt(value) * 1000)
        return formatDateTime(date)
      } else if (/^\d{13}$/.test(value)) {
        // 13位时间戳，毫秒级
        const date = new Date(parseInt(value))
        return formatDateTime(date)
      }
      
      return value
    }

    // 格式化日期时间为 yyyy-mm-dd HH:mm:ss
    const formatDateTime = (date) => {
      if (!date || isNaN(date.getTime())) return '-'
      
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    }

    // 相对时间格式化（如：30秒前、1分钟前、3小时前、2天前）
    const nowTick = ref(Date.now())
    const formatRelativeTime = (isoString) => {
      if (!isoString) return '-'
      const now = nowTick.value
      const ts = new Date(isoString).getTime()
      if (Number.isNaN(ts)) return '-'
      const diffSec = Math.max(0, Math.floor((now - ts) / 1000))
      if (diffSec < 5) return t('time.justNow')
      if (diffSec < 60) return t('time.secondsAgo', { n: diffSec })
      const diffMin = Math.floor(diffSec / 60)
      if (diffMin < 60) return t('time.minutesAgo', { n: diffMin })
      const diffHour = Math.floor(diffMin / 60)
      if (diffHour < 24) return t('time.hoursAgo', { n: diffHour })
      const diffDay = Math.floor(diffHour / 24)
      return t('time.daysAgo', { n: diffDay })
    }

    /** 金币等大数：中文/韩文用「万/만」，英文用 K（千）便于阅读 */
    const formatWanAmount = (num, fractionDigits = 2) => {
      if (!num && num !== 0) return '-'
      const n = Number(num)
      if (!Number.isFinite(n)) return '-'
      if (n < 10000) return String(n)
      const value = (n / 10000).toFixed(fractionDigits)
      return t('format.wan', { value })
    }

    // 格式化战斗力
    const formatCombatPower = (power) => {
      if (!power) return '-'
      return power.toLocaleString()
    }

    // 格式化数字（用于货币显示）
    const formatNumber = (num) => {
      if (!num && num !== 0) return '-'
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M'
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K'
      }
      return num.toString()
    }

    // 格式化银币（按万为单位）
    const formatSilver = (num) => formatWanAmount(num, 2)

    // 格式化钻石（显示完整数字，不用K/M）
    const formatDiamond = (num) => {
      if (!num && num !== 0) return '-'
      return num.toLocaleString()
    }

    // 格式化琥珀（使用千位分隔符，与战力显示一致）
    const formatAmber = (num) => {
      if (!num && num !== 0) return '-'
      // 如果是字符串，先移除所有非数字字符，再转换为数字
      if (typeof num === 'string') {
        const digitsOnly = num.replace(/[^0-9]/g, '')
        if (!digitsOnly) return '0'
        num = parseInt(digitsOnly, 10)
      }
      // 使用 toLocaleString 添加千位分隔符
      return num.toLocaleString()
    }

    // 格式化钻石数量（直接显示具体数字，使用千位分隔符）
    const formatDiamondCount = (count) => {
      if (!count && count !== 0) return '-'
      const n = Number(count)
      if (!Number.isFinite(n)) return '-'
      if (n >= 10000) {
        return formatWanAmount(n, 2)
      }
      return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }



    // Canvas相关方法已在上面定义



    // 窗口操作处理
    const handleWindowAction = async (command) => {
      const { action, role } = command
      
      try {
        if (action === 'screenshot') {
          await handleScreenshot(role)
        } else if (action === 'restart') {
          await handleRestartWindow(role)
        } else if (action === 'view_logs') {
          handleViewLogs(role)
        } else if (action === 'diamond_trend') {
          handleDiamondTrend(role)
        } else if (action === 'view_status') {
          handleViewStatus(role)
        }
      } catch (error) {
        console.error('窗口操作失败:', error)
        ElMessage.error('操作失败: ' + (error.message || '未知错误'))
      }
    }

    const handleScreenshot = async (role) => {
      try {
        await ElMessageBox.confirm(
          `确定要为角色 "${role.name || '未知角色'}" 进行截图吗？`,
          '确认截图',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning',
          }
        )

        await createClearMainQuestNotification(role.id)
        ElMessage.success(`角色 "${role.name}" 的截图命令已发送`)
      } catch (error) {
        if (error !== 'cancel') {
          throw error
        }
      }
    }

    // 重启窗口
    const handleRestartWindow = async (role) => {
      try {
        await ElMessageBox.confirm(
          `确定要重启窗口 #${role.window_id} (${role.name || '未知角色'}) 吗？`,
          '确认重启',
          {
            confirmButtonText: '确定重启',
            cancelButtonText: '取消',
            type: 'warning',
          }
        )
        
        // TODO: 调用重启窗口的API
        ElMessage.success(`窗口 #${role.window_id} 重启命令已发送`)
        
        // 3秒后刷新数据
        setTimeout(() => {
          loadData()
        }, 3000)
        
      } catch (error) {
        if (error !== 'cancel') {
          throw error
        }
      }
    }

    // 日志相关状态
    const logDialogVisible = ref(false)
    const logLoading = ref(false)
    const currentRole = ref(null)
    const logData = ref({
      logs: [],
      total: 0
    })

    // 钻石走势相关状态
    const diamondTrendDialogVisible = ref(false)
    const diamondTrendLoading = ref(false)
    const currentDiamondTrendRole = ref(null)
    const diamondTrendData = ref([])
    const selectedWeeks = ref(1) // 默认1周
    const diamondTrendChartRef = ref(null)
    const diamondTrendChartInstance = ref(null)
    const currentPage = ref(1)
    const pageSize = ref(50) // 每页显示数量
    
    // 时间选择器状态
    const timeRange = ref([])
    const timeRangeOptions = [
      { label: '最近1小时', value: 1 },
      { label: '最近3小时', value: 3 },
      { label: '最近6小时', value: 6 },
      { label: '最近12小时', value: 12 },
      { label: '最近1天', value: 24 },
      { label: '最近2天', value: 48 },
      { label: '最近7天', value: 168 }
    ]
    const selectedTimeRange = ref(6) // 默认6小时

    // 查看日志
    const handleViewLogs = async (role) => {
      currentRole.value = role
      currentPage.value = 1
      logDialogVisible.value = true
      await loadLogs()
    }

    // 加载日志数据
    const loadLogs = async () => {
      if (!currentRole.value) return
      
      logLoading.value = true
      try {
        // 计算时间范围
        const now = Date.now()
        const fromTime = Math.floor((now - selectedTimeRange.value * 60 * 60 * 1000) / 1000)
        const toTime = Math.floor(now / 1000)

        const response = await getWindowLogs(
          currentRole.value.window_id.toString(),
          currentPage.value,
          pageSize.value,
          fromTime,
          toTime
        )
        
        if (response.data) {
          logData.value.logs = response.data.logs || []
          logData.value.total = response.data.total || 0
        }
      } catch (error) {
        console.error('加载日志失败:', error)
        ElMessage.error('加载日志失败: ' + (error.message || '未知错误'))
      } finally {
        logLoading.value = false
      }
    }

    // 切换日志页码
    const handleLogPageChange = async (page) => {
      console.log('页码变化被触发:', page)
      currentPage.value = page
      await loadLogs()
    }

    // 切换每页显示数量
    const handleLogSizeChange = async (size) => {
      pageSize.value = size
      currentPage.value = 1 // 重置到第一页
      await loadLogs()
    }

    // 查看钻石走势
    const handleDiamondTrend = async (role) => {
      currentDiamondTrendRole.value = role
      selectedWeeks.value = 1 // 重置为1周
      diamondTrendDialogVisible.value = true
      await loadDiamondTrend()
    }

    // 加载钻石走势数据
    const loadDiamondTrend = async () => {
      if (!currentDiamondTrendRole.value) return
      
      diamondTrendLoading.value = true
      try {
        const response = await getDiamondTrend(
          currentDiamondTrendRole.value.window_id.toString(),
          selectedWeeks.value
        )
        
        if (response.data) {
          diamondTrendData.value = response.data || []
          console.log('钻石走势数据加载完成:', diamondTrendData.value.length, '条记录')
          // 等待 DOM 更新和对话框完全渲染
          await nextTick()
          // 再等待一小段时间确保图表容器已经渲染
          setTimeout(() => {
            console.log('开始渲染图表，容器状态:', {
              chartRef: diamondTrendChartRef.value,
              dataLength: diamondTrendData.value.length
            })
            renderDiamondTrendChart()
          }, 300)
        }
      } catch (error) {
        console.error('加载钻石走势失败:', error)
        ElMessage.error('加载钻石走势失败: ' + (error.message || '未知错误'))
      } finally {
        diamondTrendLoading.value = false
      }
    }

    // 渲染钻石走势图表
    const renderDiamondTrendChart = () => {
      if (!diamondTrendChartRef.value || diamondTrendData.value.length === 0) {
        console.log('图表容器未准备好或数据为空', {
          chartRef: diamondTrendChartRef.value,
          dataLength: diamondTrendData.value.length
        })
        return
      }

      // 销毁旧图表
      if (diamondTrendChartInstance.value) {
        diamondTrendChartInstance.value.dispose()
        diamondTrendChartInstance.value = null
      }

      // 创建新图表
      try {
        diamondTrendChartInstance.value = echarts.init(diamondTrendChartRef.value)
      } catch (error) {
        console.error('初始化图表失败:', error)
        return
      }

      // 准备数据
      const times = diamondTrendData.value.map(item => item.time)
      const diamonds = diamondTrendData.value.map(item => item.diamond)
      
      console.log('准备渲染图表，数据点数量:', times.length)

      // 配置选项
      const option = {
        tooltip: {
          trigger: 'axis',
          show: true,
          showContent: true,
          formatter: (params) => {
            if (!params || params.length === 0) return ''
            const param = params[0]
            const time = param.axisValue || ''
            const diamond = param.value || 0
            return `时间: ${time}<br/>钻石数: ${diamond.toLocaleString()}`
          },
          axisPointer: {
            type: 'line',
            lineStyle: {
              color: '#409EFF',
              width: 1,
              type: 'dashed'
            },
            label: {
              show: true,
              backgroundColor: '#409EFF',
              color: '#fff'
            }
          },
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#409EFF',
          borderWidth: 1,
          textStyle: {
            color: '#fff',
            fontSize: 12
          },
          padding: [8, 12]
        },
        grid: {
          left: '5%',
          right: '5%',
          top: '10%',
          bottom: '20%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: times,
          axisLabel: {
            rotate: 45,
            fontSize: 12,
            // 根据数据点数量动态计算间隔
            // interval: 0 表示显示所有，数值越大标签越稀疏
            interval: (() => {
              const totalPoints = times.length
              // 数据点 ≤ 10：显示所有标签
              if (totalPoints <= 10) {
                return 0
              }
              // 数据点 ≤ 30：每隔2个显示（interval: 3）
              if (totalPoints <= 30) {
                return 3
              }
              // 数据点 ≤ 60：每隔3个显示（interval: 4）
              if (totalPoints <= 60) {
                return 4
              }
              // 数据点 > 60：每隔4个显示（interval: 5）
              return 5
            })(),
            showMinLabel: true,
            showMaxLabel: true
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value) => {
              if (value >= 10000) {
                return formatWanAmount(value, 1)
              }
              return value.toLocaleString()
            }
          }
        },
        series: [
          {
            name: '钻石数量',
            type: 'line',
            smooth: true,
            data: diamonds,
            symbol: 'circle',
            symbolSize: 4,
            itemStyle: {
              color: '#409EFF'
            },
            lineStyle: {
              width: 2,
              color: '#409EFF'
            },
            emphasis: {
              focus: 'series',
              itemStyle: {
                color: '#409EFF',
                borderColor: '#fff',
                borderWidth: 2
              }
            },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(64, 158, 255, 0.3)' },
                  { offset: 1, color: 'rgba(64, 158, 255, 0.1)' }
                ]
              }
            }
          }
        ]
      }

      diamondTrendChartInstance.value.setOption(option)
      
      // 确保图表正确渲染
      setTimeout(() => {
        if (diamondTrendChartInstance.value) {
          diamondTrendChartInstance.value.resize()
        }
      }, 100)

      // 响应式调整
      window.addEventListener('resize', () => {
        if (diamondTrendChartInstance.value) {
          diamondTrendChartInstance.value.resize()
        }
      })
    }
    
    // 监听对话框打开状态，确保图表容器准备好后再渲染
    watch(diamondTrendDialogVisible, (newVal) => {
      if (newVal && diamondTrendData.value.length > 0) {
        console.log('对话框打开，准备渲染图表，数据量:', diamondTrendData.value.length)
        // 对话框打开后，等待 DOM 更新
        nextTick(() => {
          setTimeout(() => {
            console.log('对话框已打开，开始渲染图表，容器:', diamondTrendChartRef.value)
            renderDiamondTrendChart()
          }, 300)
        })
      }
    })

    //  添加格式化时间方法 - 去掉年月日，只保留时分秒
    const formatTime = (timeString) => {
      if (!timeString) return '-'
      try {
        const date = new Date(timeString)
        return date.toLocaleTimeString('zh-CN', { 
          hour12: false,
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        })
      } catch (error) {
        return timeString
      }
    }

    // 缓存最新的设备信息，避免重复API调用
    let cachedDevicesData = null
    let lastDevicesUpdateTime = 0
    const DEVICES_CACHE_DURATION = 5000 // 5秒缓存时间

    // 获取最新设备信息（带缓存）
    const getLatestDevicesInfo = async () => {
      const now = Date.now()
      
      // 如果缓存未过期，直接返回缓存数据
      if (cachedDevicesData && (now - lastDevicesUpdateTime) < DEVICES_CACHE_DURATION) {
        return cachedDevicesData
      }
      
      try {
        const { getDevicesInfo } = await import('@/api/roles')
        const response = await getDevicesInfo()
        
        if (response && response.data) {
          cachedDevicesData = response.data
          lastDevicesUpdateTime = now
          return cachedDevicesData
        }
      } catch (error) {
        console.warn('获取设备信息失败:', error)
      }
      
      return null
    }

    // 刷新单个图片（预加载新的OSS预签名URL避免闪烁）
    const refreshSingleImage = async (deviceId) => {
      const device = windowList.value.find(role => role.id === deviceId)
      if (!device) {
        return
      }

      try {
        // 使用缓存获取最新的设备信息
        const devicesData = await getLatestDevicesInfo()
        
        if (devicesData) {
          // 找到对应设备的新URL
          const updatedDevice = devicesData.find(d => d.id === deviceId)
          if (updatedDevice && updatedDevice.screenshot_url) {
            // 预加载新图片
            const img = new Image()
            img.onload = () => {
              // 图片加载完成后，更新设备信息
              const index = windowList.value.findIndex(role => role.id === deviceId)
              if (index !== -1) {
                windowList.value[index].screenshot_url = updatedDevice.screenshot_url
              }
            }
            img.src = updatedDevice.screenshot_url
          }
        }
      } catch (error) {
        console.warn('静默刷新图片失败:', error)
      }
    }

    // 刷新所有图片（移除API调用，只刷新本地截图流）
    const refreshAllImages = async () => {
      console.log('开始刷新本地截图流...')
      
      try {
        // 不再调用 api/roles/devices 接口
        // 只刷新本地的MJPEG流和canvas绘制
        
        // 对于有本地IP且status=1的设备，刷新MJPEG流
        windowList.value.forEach(device => {
          const canvas = canvasRefs.value.get(device.id)
          if (canvas && device.datas?.ip) {
            // 检查是否已缓存为本地服务可用
            const localIp = device.datas.ip
            const isLocalActive = localStatusCache.get(localIp)
            
            if (isLocalActive) {
              // 本地服务可用，MJPEG流会自动更新，无需额外操作
              console.log(`设备 ${device.id} 本地MJPEG流正在自动更新`)
            }
          }
        })
        
        console.log('本地截图流刷新完成')
        
      } catch (error) {
        console.warn('刷新本地截图流失败:', error)
      }
    }

    // 全局图片刷新定时器
    let globalImageRefreshTimer = null

    // 启动图片自动刷新（优化版：使用单个全局定时器）
    const startImageAutoRefresh = () => {
      // 先清除现有的全局定时器
      if (globalImageRefreshTimer) {
        clearInterval(globalImageRefreshTimer)
        globalImageRefreshTimer = null
      }

      // 创建单个全局定时器，每20秒刷新所有图片（OSS预签名URL 5分钟过期）
      globalImageRefreshTimer = setInterval(() => {
        refreshAllImages()
      }, 20 * 1000) // 每20秒刷新一次，确保在OSS URL过期前更新
    }

    // 停止所有图片自动刷新
    const stopImageAutoRefresh = () => {
      // 清除旧的单个窗口定时器（兼容性）
      imageRefreshTimers.forEach(timer => clearInterval(timer))
      imageRefreshTimers.clear()
      
      // 清除全局定时器
      if (globalImageRefreshTimer) {
        clearInterval(globalImageRefreshTimer)
        globalImageRefreshTimer = null
      }
    }

    // 手动刷新处理
    const handleManualRefresh = async () => {
      // 检查是否在5秒冷却时间内
      if (refreshCooldown.value) {
        ElMessage.warning('请等待5秒后再刷新')
        return
      }
      
      // 设置5秒冷却时间和倒计时
      refreshCooldown.value = true
      refreshCountdown.value = 5
      
      // 倒计时定时器
      const countdownTimer = setInterval(() => {
        refreshCountdown.value--
        if (refreshCountdown.value <= 0) {
          clearInterval(countdownTimer)
          refreshCooldown.value = false
        }
      }, 1000)
      
      // 执行数据刷新
      await loadData()
      
      // 刷新所有图片
      refreshAllImages()
    }

    // 批量选择相关方法
    const handleSelectAll = (value) => {
      if (value) {
        selectedRoles.value = filteredWindowList.value.map(role => role.id)
      } else {
        selectedRoles.value = []
      }
    }

    const handleRoleSelect = (roleId, checked) => {
      if (checked) {
        if (!selectedRoles.value.includes(roleId)) {
          selectedRoles.value.push(roleId)
        }
      } else {
        const index = selectedRoles.value.indexOf(roleId)
        if (index > -1) {
          selectedRoles.value.splice(index, 1)
        }
      }
      
      // 更新全选状态
      selectAll.value = selectedRoles.value.length === filteredWindowList.value.length
    }

    const getSelectedRoles = () => {
      return windowList.value.filter(role => selectedRoles.value.includes(role.id))
    }

    // 格式化选中窗口的显示文本
    const formatSelectedWindowsText = () => {
      const selected = getSelectedRoles()
      if (selected.length <= 5) {
        return selected.map(role => `#${role.window_id}`).join('、')
      } else {
        const first5 = selected.slice(0, 5).map(role => `#${role.window_id}`).join('、')
        return `${first5} 等${selected.length}个`
      }
    }

    // 显示批量配置对话框
    const showBatchConfigDialog = async () => {
      if (selectedRoles.value.length === 0) {
        ElMessage.warning('请先选择要更换配置的窗口')
        return
      }
      batchConfigDialogVisible.value = true
      selectedConfigId.value = ''
      await loadAvailableConfigs()
    }

    // 加载可用配置列表
    const loadAvailableConfigs = async () => {
      configsLoading.value = true
      try {
        const response = await getUserConfigs()
        availableConfigs.value = response.data || []
      } catch (error) {
        console.error('加载配置列表失败:', error)
        ElMessage.error('加载配置列表失败: ' + (error.message || '未知错误'))
      } finally {
        configsLoading.value = false
      }
    }

    // 加载配置列表（用于筛选）
    const loadConfigs = async () => {
      try {
        // 如果是查看其他用户的窗口，加载该用户的配置列表
        const userId = targetUserId.value
        const response = await getUserConfigs(null, null, userId)
        availableConfigs.value = response.data || []
      } catch (error) {
        console.error('加载配置列表失败:', error)
        const errorMessage = error.response?.data?.error ||
                          error.response?.data?.message ||
                          error.message ||
                          '未知错误'
        ElMessage.error('加载配置列表失败: ' + errorMessage)
      }
    }

    // 批量更新配置
    const handleBatchConfigUpdate = async () => {
      if (!selectedConfigId.value) {
        ElMessage.warning('请选择要更换的配置')
        return
      }

      try {
        await ElMessageBox.confirm(
          `确定要将选中的 ${selectedRoles.value.length} 个窗口的配置更换为 "${availableConfigs.value.find(c => c.id === selectedConfigId.value)?.name}" 吗？`,
          '确认批量更换配置',
          {
            confirmButtonText: '确定更换',
            cancelButtonText: '取消',
            type: 'warning',
          }
        )

        batchUpdating.value = true
        
        await batchUpdateRoleConfigs({
          roleIds: selectedRoles.value,
          configId: selectedConfigId.value
        })

        ElMessage.success(`成功更换 ${selectedRoles.value.length} 个窗口的配置`)
        batchConfigDialogVisible.value = false
        selectedRoles.value = []
        selectAll.value = false
        
        // 刷新数据以显示最新配置
        await loadData()
        
      } catch (error) {
        if (error !== 'cancel') {
          console.error('批量更换配置失败:', error)
          ElMessage.error('批量更换配置失败: ' + (error.message || '未知错误'))
        }
      } finally {
        batchUpdating.value = false
      }
    }

    // 显示批量删除对话框
    const showBatchDeleteDialog = () => {
      if (selectedRoles.value.length === 0) {
        ElMessage.warning('请先选择要删除的窗口')
        return
      }
      batchDeleteDialogVisible.value = true
    }

    // 加载用户分组列表
    const loadUserGroups = async () => {
      try {
        const response = await getUserGroups()
        console.log('getUserGroups API响应:', response)
        // 检查响应数据结构
        if (response && response.data) {
          userGroups.value = response.data
        } else if (Array.isArray(response)) {
          // 如果响应拦截器直接返回了数组
          userGroups.value = response
        } else {
          userGroups.value = []
        }
        console.log('更新后的userGroups:', userGroups.value)
      } catch (error) {
        console.error('加载分组列表失败:', error)
      }
    }

    // 显示批量分组对话框
    const showBatchGroupDialog = async () => {
      if (selectedRoles.value.length === 0) {
        ElMessage.warning('请先选择要设置分组的窗口')
        return
      }
      await loadUserGroups()
      selectedGroupName.value = null
      batchGroupDialogVisible.value = true
    }

    const showBatchAiAnswerDialog = () => {
      if (selectedRoles.value.length === 0) {
        ElMessage.warning('请先选择要设置的窗口')
        return
      }
      batchAiAnswerText.value = ''
      batchAiAnswerDialogVisible.value = true
    }

    const handleBatchAiAnswerUpdate = async () => {
      if (selectedRoles.value.length === 0) {
        ElMessage.warning('请先选择要设置的窗口')
        return
      }
      batchAiAnswerUpdating.value = true
      try {
        const roleIds = selectedRoles.value.filter(id => id != null)
        if (roleIds.length === 0) {
          ElMessage.warning('没有有效的角色ID')
          return
        }
        const text = batchAiAnswerText.value == null ? '' : String(batchAiAnswerText.value).trim()
        const res = await batchUpdateAiAnswerCustom(roleIds, text)
        if (res.code === 200) {
          const updated = res.data?.updated ?? roleIds.length
          ElMessage.success(`成功设置 ${updated} 个窗口`)
          batchAiAnswerDialogVisible.value = false
          batchAiAnswerText.value = ''
          selectedRoles.value = []
          selectAll.value = false
          await loadData()
        } else {
          ElMessage.error(res.message || '批量设置失败')
        }
      } catch (error) {
        ElMessage.error(error.response?.data?.message || error.message || '批量设置失败')
      } finally {
        batchAiAnswerUpdating.value = false
      }
    }

    // 批量设置分组
    const handleBatchGroupUpdate = async () => {
      if (selectedRoles.value.length === 0) {
        ElMessage.warning('请先选择要设置分组的窗口')
        return
      }

      batchGroupUpdating.value = true
      try {
        // selectedRoles.value 已经是ID数组，不需要再map
        const roleIds = selectedRoles.value.filter(id => id != null)
        if (roleIds.length === 0) {
          ElMessage.warning('没有有效的角色ID')
          return
        }
        await batchSetRoleGroup(roleIds, selectedGroupName.value)
        
        ElMessage.success(`成功设置 ${roleIds.length} 个窗口的分组`)
        batchGroupDialogVisible.value = false
        selectedRoles.value = []
        selectAll.value = false
        selectedGroupName.value = null
        
        // 刷新数据和分组列表
        await Promise.all([loadData(), loadUserGroups()])
        
      } catch (error) {
        console.error('批量设置分组失败:', error)
        ElMessage.error('批量设置分组失败: ' + (error.message || '未知错误'))
      } finally {
        batchGroupUpdating.value = false
      }
    }

    // 删除分组
    const handleDeleteGroup = async (groupName) => {
      try {
        await ElMessageBox.confirm(
          `确定要删除分组 "${groupName}" 吗？\n删除后该分组下的窗口将变成未分组状态。`,
          '删除分组',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        
        console.log('开始删除分组:', groupName)
        await deleteGroup(groupName)
        console.log('删除分组API调用成功')
        ElMessage.success('删除分组成功')
        
        // 刷新数据和分组列表
        console.log('刷新分组列表...')
        await loadUserGroups()
        console.log('分组列表刷新完成')
        
        // 刷新窗口数据
        await loadData()
        
        // 如果当前筛选的是被删除的分组，清空筛选
        if (groupFilter.value === groupName) {
          groupFilter.value = null
        }
        
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除分组失败:', error)
          ElMessage.error('删除分组失败: ' + (error.message || '未知错误'))
        }
      }
    }

    // 处理移动端批量操作
    const handleMobileBatchOperation = (command) => {
      if (selectedRoles.value.length === 0) {
        ElMessage.warning('请先选择要操作的窗口')
        return
      }
      
      switch (command) {
        case 'batch_config':
          showBatchConfigDialog()
          break
        case 'batch_group':
          showBatchGroupDialog()
          break
        case 'batch_ai_answer':
          showBatchAiAnswerDialog()
          break
        case 'batch_auto_answer_on':
          handleBatchAutoAnswer(true)
          break
        case 'batch_auto_answer_off':
          handleBatchAutoAnswer(false)
          break
        case 'batch_delete':
          showBatchDeleteDialog()
          break
        default:
          console.warn('未知的批量操作命令:', command)
      }
    }

    // 批量停止功能
    const handleBatchAutoAnswer = async (enabled) => {
      if (selectedRoles.value.length === 0) {
        ElMessage.warning('请先选择要操作的窗口')
        return
      }

      const roleIds = selectedRoles.value.slice(0, 500).filter(id => id != null)
      if (roleIds.length === 0) {
        ElMessage.warning('没有有效的角色ID')
        return
      }

      const actionText = enabled ? '开启' : '关闭'
      try {
        await ElMessageBox.confirm(
          `确定为选中的 ${roleIds.length} 个窗口${actionText}自动答题吗？`,
          `确认${actionText}自动答题`,
          {
            confirmButtonText: `确定${actionText}`,
            cancelButtonText: '取消',
            type: 'warning',
          }
        )

        const res = await batchUpdateAutoAnswerEnabled(roleIds, enabled)
        if (res.code === 200) {
          const updated = res.data?.updated ?? roleIds.length
          ElMessage.success(`已为 ${updated} 个窗口${actionText}自动答题`)
          selectedRoles.value = []
          selectAll.value = false
          await loadData()
        } else {
          ElMessage.error(res.message || `批量${actionText}失败`)
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error(error.response?.data?.message || error.message || `批量${actionText}失败`)
        }
      }
    }

    // 批量删除账号
    const handleBatchDelete = async () => {
      try {
        await ElMessageBox.confirm(
          `确定要删除选中的 ${selectedRoles.value.length} 个账号吗？此操作不可撤销！`,
          '确认批量删除',
          {
            confirmButtonText: '确认删除',
            cancelButtonText: '取消',
            type: 'error',
          }
        )

        batchDeleting.value = true
        
        // 调用批量删除API
        const result = await batchDeleteRoles(selectedRoles.value)
        
        ElMessage.success(`成功删除 ${selectedRoles.value.length} 个账号`)
        batchDeleteDialogVisible.value = false
        selectedRoles.value = []
        selectAll.value = false
        
        // 刷新数据
        await loadData()
        
      } catch (error) {
        if (error !== 'cancel') {
          console.error('批量删除失败:', error)
          ElMessage.error('批量删除失败: ' + (error.message || '未知错误'))
        }
      } finally {
        batchDeleting.value = false
      }
    }

    // 同步滚动的引用
    const scrollableHeader = ref(null)
    const scrollableBody = ref(null)
    const frozenBody = ref(null)
    let isScrollingSyncing = false
    
    // 悬停行索引，用于同步整行悬停效果
    const hoveredRowIndex = ref(null)

    // 列配置管理
    const tableColumns = ref([
      { key: 'checkbox', label: '选择', visible: true, hideable: false, width: '50px', sortable: false },
      { key: 'window', label: '窗口', visible: true, hideable: false, width: '70px', sortable: true, sortKey: 'window_id' },
      { key: 'role', label: '角色', visible: true, hideable: false, width: '130px', sortable: false },
      { key: 'class', label: '职业', visible: true, hideable: false, width: '100px', sortable: false },
      { key: 'ai_answer_custom', label: 'AI自定义答题', visible: true, hideable: true, width: '220px', sortable: false },
      { key: 'auto_answer_enabled', label: '自动答题', visible: true, hideable: true, width: '90px', sortable: false },
      { key: 'answer_test', label: '答题测试', visible: true, hideable: true, width: '80px', sortable: false },
      { key: 'level', label: '等级', visible: true, hideable: false, width: '70px', sortable: true, sortKey: 'level' },
      { key: 'silver', label: '金币', visible: true, hideable: false, width: '120px', sortable: true, sortKey: 'silver' },
      { key: 'yesterday_diamonds', label: '昨日金币', visible: true, hideable: false, width: '110px', sortable: true, sortKey: 'yesterday_diamonds' },
      { key: 'today_diamonds', label: '今日金币', visible: true, hideable: false, width: '110px', sortable: true, sortKey: 'today_diamonds' },
      { key: 'hpPotionCount', label: '血瓶数量', visible: true, hideable: false, width: '90px', sortable: false },
      { key: 'mpPotionCount', label: '蓝瓶数量', visible: true, hideable: false, width: '90px', sortable: false },
      { key: 'petSnackCount', label: '宠物零食数量', visible: true, hideable: false, width: '110px', sortable: false },
      { key: 'scrollCount', label: '卷轴数量', visible: true, hideable: false, width: '90px', sortable: false },
      { key: 'status', label: '状态', visible: true, hideable: false, width: '90px', sortable: false },
      { key: 'updateTime', label: '更新时间', visible: true, hideable: false, width: '130px', sortable: false },
      { key: 'config', label: '配置名称', visible: true, hideable: false, width: '180px', sortable: false },
      { key: 'group', label: '分类', visible: true, hideable: false, width: '110px', sortable: false },
      { key: 'version', label: '版本号', visible: true, hideable: false, width: '90px', sortable: true, sortKey: 'version' }
    ])

    const sortOptions = computed(() =>
      tableColumns.value
        .filter(col => col.sortable && col.sortKey)
        .map(col => ({
          value: col.sortKey,
          label: col.label
        }))
    )

    // Cookie管理
    const COLUMN_VISIBILITY_KEY = 'window-list-columns'
    
    const setCookie = (name, value, days = 30) => {
      const expires = new Date()
      expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000))
      document.cookie = `${name}=${JSON.stringify(value)};expires=${expires.toUTCString()};path=/`
    }
    
    const getCookie = (name) => {
      const nameEQ = name + "="
      const ca = document.cookie.split(';')
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i]
        while (c.charAt(0) == ' ') c = c.substring(1, c.length)
        if (c.indexOf(nameEQ) == 0) {
          try {
            return JSON.parse(c.substring(nameEQ.length, c.length))
          } catch (e) {
            return null
          }
        }
      }
      return null
    }

    // 从cookie读取页面大小设置，默认100
    const savedPageSize = getSimpleCookie('windowSettings_pageSize')
    if (savedPageSize) {
      devicePagination.value.size = parseInt(savedPageSize)
    }

    // 加载保存的列可见性设置
    const loadColumnVisibility = () => {
      const savedColumns = getCookie(COLUMN_VISIBILITY_KEY)
      if (savedColumns) {
        tableColumns.value.forEach(column => {
          const saved = savedColumns.find(sc => sc.key === column.key)
          if (saved && column.hideable) {
            column.visible = saved.visible
          }
        })
      }
    }

    // 保存列可见性设置
    const saveColumnVisibility = () => {
      const columnsToSave = tableColumns.value.map(col => ({
        key: col.key,
        visible: col.visible
      }))
      setCookie(COLUMN_VISIBILITY_KEY, columnsToSave)
    }

    // 更新列可见性
    const updateColumnVisibility = () => {
      saveColumnVisibility()
    }


    // 获取可见的滚动列
    const visibleScrollableColumns = computed(() => {
      return tableColumns.value.filter(col => 
        col.visible && !['checkbox', 'window'].includes(col.key)
      )
    })

    // 获取可见的所有列（包括窗口列，用于导出）
    const visibleColumnsForExport = computed(() => {
      return tableColumns.value.filter(col => 
        col.visible && col.key !== 'checkbox'
      )
    })

    // 获取单元格的值（用于导出Excel）
    const getCellValue = (role, column) => {
      switch (column.key) {
        case 'window':
          return role.window_id || '-'
        case 'role':
          return role.name || '-'
        case 'class':
          return role.datas?.vocation_name || '-'
        case 'ai_answer_custom':
          return role.datas?.ai_answer_custom || '-'
        case 'auto_answer_enabled':
          return getRoleAutoAnswerEnabled(role) ? '是' : '否'
        case 'region':
          return role.region || '-'
        case 'level':
          return role.level || '-'
        case 'combat':
          return formatCombatPower(role.combat_power)
        case 'guild':
          if (role.datas?.['公会等级'] && role.datas?.['公会身份']) {
            return `${role.datas['公会等级']}:${role.datas['公会身份']}`
          }
          return '-'
        case 'equipment':
          const equipments = []
          if (role.datas?.女武神卡) equipments.push(`女武神:${role.datas.女武神卡}`)
          if (role.datas?.飞鸟) equipments.push(`飞鸟:${role.datas.飞鸟}`)
          if (role.datas?.坐骑) equipments.push(`坐骑:${role.datas.坐骑}`)
          if (role.datas?.宠物) equipments.push(`宠物:${role.datas.宠物}`)
          return equipments.length > 0 ? equipments.join(' ') : '-'
        case 'diamond':
          return formatDiamond(role.datas?.diamond)
        case 'yesterday_diamonds':
          return formatDiamond(role.yesterday_diamonds)
        case 'today_diamonds':
          return formatDiamond(role.today_diamonds)
        case 'gwemix':
          return role.datas?.gwemix || '-'
        case 'amber':
          return formatAmber(role.datas?.amber2)
        case 'silver':
          return formatSilver(role.datas?.silver)
        case 'pending':
          const pending = []
          if (role.datas?.伙伴待领 === '待领') pending.push('伙伴')
          if (role.datas?.宠物待领 === '待领') pending.push('宠物')
          if (role.datas?.女武神卡待领 === '待领') pending.push('女武神')
          return pending.length > 0 ? pending.join(' ') : '-'
        case 'worldExchange':
          const worldExchangeParts = []
          if (role.datas?.世界交易所 && role.datas.世界交易所 !== null && role.datas.世界交易所 !== '') {
            worldExchangeParts.push(role.datas.世界交易所)
          }
          if (role.datas?.['世界交易所-结算'] && role.datas?.['世界交易所-结算'] !== null && role.datas?.['世界交易所-结算'] !== '') {
            worldExchangeParts.push(`有结算:${role.datas['世界交易所-结算']}`)
          }
          return worldExchangeParts.length > 0 ? worldExchangeParts.join(' ') : '-'
        case 'normalExchange':
          return role.datas?.普通交易所 || '-'
        case 'game':
          return role.game || '-'
        case 'device':
          return role.device_id || '-'
        case 'version':
          return role.version || '-'
        case 'hpPotionCount':
          return formatDatasCount(role.datas, '血瓶数量')
        case 'mpPotionCount':
          return formatDatasCount(role.datas, '蓝瓶数量')
        case 'petSnackCount':
          return formatDatasCount(role.datas, '宠物零食数量')
        case 'scrollCount':
          return formatDatasCount(role.datas, '卷轴数量')
        case 'status':
          return getStatusText(role)
        case 'updateTime':
          return formatRelativeTime(role.update_time)
        case 'config':
          return role.config_name || '-'
        case 'group':
          return role.group_name || '-'
        default:
          return '-'
      }
    }

    // 导出Excel
    const exportToExcel = async () => {
      try {
        // 动态导入xlsx库
        const XLSX = await import('xlsx').catch(() => {
          ElMessage.error('请先安装xlsx库: npm install xlsx')
          return null
        })
        
        if (!XLSX) return

        if (filteredWindowList.value.length === 0) {
          ElMessage.warning('没有数据可导出')
          return
        }

        // 准备表头
        const headers = visibleColumnsForExport.value.map(col => col.label.replace(/[🪟👤⚔️🌍📊⚡🏛️🎒💎📅📆💰🟠🪙🎁🌐🏪🎮📱🔖💀🔄🕐📋⚙️📁]/g, '').trim())
        
        // 准备数据
        const data = filteredWindowList.value.map(role => {
          return visibleColumnsForExport.value.map(column => {
            return getCellValue(role, column)
          })
        })

        // 创建工作簿
        const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data])
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, '窗口列表')

        // 设置列宽
        const colWidths = visibleColumnsForExport.value.map(col => ({ wch: 15 }))
        worksheet['!cols'] = colWidths

        // 导出文件
        const fileName = `窗口列表_${new Date().toISOString().slice(0, 10)}.xlsx`
        XLSX.writeFile(workbook, fileName)

        ElMessage.success(`成功导出 ${filteredWindowList.value.length} 条数据`)
      } catch (error) {
        console.error('导出Excel失败:', error)
        ElMessage.error('导出Excel失败: ' + (error.message || '未知错误'))
      }
    }

    // 生成动态grid-template-columns
    const dynamicGridColumns = computed(() => {
      return visibleScrollableColumns.value.map(col => col.width).join(' ')
    })

    // 同步表头滚动
    const syncHeaderScroll = (event) => {
      if (isScrollingSyncing) return
      isScrollingSyncing = true
      const scrollLeft = event.target.scrollLeft
      if (scrollableBody.value && Math.abs(scrollableBody.value.scrollLeft - scrollLeft) > 1) {
        scrollableBody.value.scrollLeft = scrollLeft
      }
      setTimeout(() => {
        isScrollingSyncing = false
      }, 10)
    }

    // 同步表体滚动
    const syncBodyScroll = (event) => {
      if (isScrollingSyncing) return
      isScrollingSyncing = true
      
      const scrollLeft = event.target.scrollLeft
      const scrollTop = event.target.scrollTop
      
      // 同步水平滚动到表头
      if (scrollableHeader.value && Math.abs(scrollableHeader.value.scrollLeft - scrollLeft) > 1) {
        scrollableHeader.value.scrollLeft = scrollLeft
      }
      
      // 同步垂直滚动到冻结列
      if (frozenBody.value && Math.abs(frozenBody.value.scrollTop - scrollTop) > 1) {
        frozenBody.value.scrollTop = scrollTop
      }
      
      setTimeout(() => {
        isScrollingSyncing = false
      }, 10)
    }

    // 同步冻结列滚动
    const syncFrozenScroll = (event) => {
      if (isScrollingSyncing) return
      isScrollingSyncing = true
      
      const scrollTop = event.target.scrollTop
      
      // 同步垂直滚动到滚动列
      if (scrollableBody.value && Math.abs(scrollableBody.value.scrollTop - scrollTop) > 1) {
        scrollableBody.value.scrollTop = scrollTop
      }
      
      setTimeout(() => {
        isScrollingSyncing = false
      }, 10)
    }

    onMounted(async () => {
      // 加载列可见性设置
      loadColumnVisibility()
      
      // 加载配置列表
      await loadConfigs()
      
      // 加载分组列表
      await loadUserGroups()
      
      await loadData()
      // 每30秒刷新一次相对时间显示
      relativeTimeInterval = setInterval(() => {
        nowTick.value = Date.now()
      }, 30000)
    })

    // 分页处理函数
    const handleDevicePageChange = (page) => {
      devicePagination.value.page = page
      loadData()
    }
    
    const handleDeviceSizeChange = (size) => {
      console.log('handleDeviceSizeChange called with:', size, typeof size)
      const newSize = typeof size === 'number' ? size : parseInt(size)
      if (isNaN(newSize)) {
        console.error('Invalid page size:', size)
        return
      }
      console.log('Setting page size to:', newSize)
      devicePagination.value.size = newSize
      devicePagination.value.page = 1 // 重置到第一页
      
      // 保存到cookie（使用简单的字符串保存）
      const expires = new Date()
      expires.setTime(expires.getTime() + (30 * 24 * 60 * 60 * 1000))
      document.cookie = `windowSettings_pageSize=${newSize};expires=${expires.toUTCString()};path=/`
      
      loadData()
    }
    
    // 配置筛选处理
    const handleConfigFilter = () => {
      devicePagination.value.page = 1 // 重置到第一页
      loadData()
    }

    const roleNameSavingIds = ref(new Set())
    const roleNameLastSaved = ref(new Map())
    const vocationNameSavingIds = ref(new Set())
    const vocationNameLastSaved = ref(new Map())
    const aiAnswerCustomSavingIds = ref(new Set())
    const aiAnswerCustomLastSaved = ref(new Map())
    const autoAnswerSavingIds = ref(new Set())

    const getRoleVocationName = (row) => {
      const vocation = row?.datas?.vocation_name
      return vocation == null ? '' : String(vocation)
    }

    const setRoleVocationName = (row, value) => {
      if (!row.datas) row.datas = {}
      row.datas.vocation_name = value
    }

    const getRoleAiAnswerCustom = (row) => {
      const text = row?.datas?.ai_answer_custom
      return text == null ? '' : String(text)
    }

    const setRoleAiAnswerCustom = (row, value) => {
      if (!row.datas) row.datas = {}
      row.datas.ai_answer_custom = value
    }

    const getRoleAutoAnswerEnabled = (row) => {
      const value = row?.datas?.auto_answer_enabled
      if (value === true || value === 1 || value === '1' || value === 'true') return true
      if (value === false || value === 0 || value === '0' || value === 'false') return false
      return false
    }

    const setRoleAutoAnswerEnabled = (row, value) => {
      if (!row.datas) row.datas = {}
      row.datas.auto_answer_enabled = !!value
    }

    const handleAutoAnswerToggle = async (row, enabled) => {
      if (isViewMode.value || !row?.id) return
      if (autoAnswerSavingIds.value.has(row.id)) return
      const next = !!enabled
      const prev = getRoleAutoAnswerEnabled(row)
      setRoleAutoAnswerEnabled(row, next)
      autoAnswerSavingIds.value.add(row.id)
      try {
        const res = await updateRole({ id: row.id, datas: { auto_answer_enabled: next } })
        if (res.code !== 200) {
          setRoleAutoAnswerEnabled(row, prev)
          ElMessage.error(res.message || '保存自动答题开关失败')
        }
      } catch (e) {
        setRoleAutoAnswerEnabled(row, prev)
        ElMessage.error(e.response?.data?.message || e.message || '保存自动答题开关失败')
      } finally {
        autoAnswerSavingIds.value.delete(row.id)
      }
    }

    const openAnswerTestDialog = (row) => {
      answerTestRole.value = row
      answerTestDialogVisible.value = true
    }

    const openAiAnswerDialog = (row) => {
      aiAnswerEditingRow.value = row
      aiAnswerEditText.value = getRoleAiAnswerCustom(row)
      aiAnswerDialogVisible.value = true
    }

    const handleAiAnswerDialogSave = async () => {
      const row = aiAnswerEditingRow.value
      if (isViewMode.value || !row?.id) {
        aiAnswerDialogVisible.value = false
        return
      }
      const text = aiAnswerEditText.value == null ? '' : String(aiAnswerEditText.value).trim()
      if (aiAnswerCustomLastSaved.value.get(row.id) === text) {
        aiAnswerDialogVisible.value = false
        return
      }
      if (aiAnswerCustomSavingIds.value.has(row.id)) return
      aiAnswerCustomSavingIds.value.add(row.id)
      aiAnswerDialogSaving.value = true
      try {
        const res = await updateRole({ id: row.id, datas: { ai_answer_custom: text } })
        if (res.code === 200) {
          setRoleAiAnswerCustom(row, text)
          aiAnswerCustomLastSaved.value.set(row.id, text)
          aiAnswerDialogVisible.value = false
        } else {
          ElMessage.error(res.message || '保存 AI 自定义答题失败')
        }
      } catch (e) {
        ElMessage.error(e.response?.data?.message || e.message || '保存 AI 自定义答题失败')
      } finally {
        aiAnswerCustomSavingIds.value.delete(row.id)
        aiAnswerDialogSaving.value = false
      }
    }

    const saveRoleName = async (row) => {
      if (isViewMode.value || !row?.id) return
      const name = row.name == null ? '' : String(row.name).trim()
      if (roleNameLastSaved.value.get(row.id) === name) return
      if (roleNameSavingIds.value.has(row.id)) return
      roleNameSavingIds.value.add(row.id)
      try {
        const res = await updateRole({ id: row.id, name })
        if (res.code === 200) {
          row.name = name
          roleNameLastSaved.value.set(row.id, name)
        } else {
          ElMessage.error(res.message || '保存角色名称失败')
        }
      } catch (e) {
        ElMessage.error(e.response?.data?.message || e.message || '保存角色名称失败')
      } finally {
        roleNameSavingIds.value.delete(row.id)
      }
    }

    const saveRoleVocation = async (row) => {
      if (isViewMode.value || !row?.id) return
      const vocationName = getRoleVocationName(row).trim()
      if (vocationNameLastSaved.value.get(row.id) === vocationName) return
      if (vocationNameSavingIds.value.has(row.id)) return
      vocationNameSavingIds.value.add(row.id)
      try {
        const res = await updateRole({ id: row.id, datas: { vocation_name: vocationName } })
        if (res.code === 200) {
          setRoleVocationName(row, vocationName)
          vocationNameLastSaved.value.set(row.id, vocationName)
        } else {
          ElMessage.error(res.message || '保存职业失败')
        }
      } catch (e) {
        ElMessage.error(e.response?.data?.message || e.message || '保存职业失败')
      } finally {
        vocationNameSavingIds.value.delete(row.id)
      }
    }

    const saveRoleAiAnswerCustom = async (row) => {
      if (isViewMode.value || !row?.id) return
      const text = getRoleAiAnswerCustom(row).trim()
      if (aiAnswerCustomLastSaved.value.get(row.id) === text) return
      if (aiAnswerCustomSavingIds.value.has(row.id)) return
      aiAnswerCustomSavingIds.value.add(row.id)
      try {
        const res = await updateRole({ id: row.id, datas: { ai_answer_custom: text } })
        if (res.code === 200) {
          setRoleAiAnswerCustom(row, text)
          aiAnswerCustomLastSaved.value.set(row.id, text)
        } else {
          ElMessage.error(res.message || '保存 AI 自定义答题失败')
        }
      } catch (e) {
        ElMessage.error(e.response?.data?.message || e.message || '保存 AI 自定义答题失败')
      } finally {
        aiAnswerCustomSavingIds.value.delete(row.id)
      }
    }
    
    // 分组筛选处理
    const handleGroupFilter = async () => {
      // 如果选择的是"新增分组"
      if (groupFilter.value === '__add_group__') {
        try {
          const { value } = await ElMessageBox.prompt('请输入新分组名称', '新增分组', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            inputValidator: (value) => {
              if (!value || value.trim() === '') {
                return '分组名称不能为空'
              }
              if (userGroups.value.includes(value)) {
                return '该分组已存在'
              }
              return true
            }
          })
          
          const newGroupName = value
          
          // 如果用户有选中的窗口，直接为这些窗口设置新分组
          if (selectedRoles.value.length > 0) {
            try {
              batchGroupUpdating.value = true
              // selectedRoles.value 已经是ID数组，不需要再map
              const roleIds = selectedRoles.value.filter(id => id != null)
              if (roleIds.length === 0) {
                ElMessage.warning('没有有效的角色ID')
                return
              }
              await batchSetRoleGroup(roleIds, newGroupName)
              
              ElMessage.success(`成功为 ${roleIds.length} 个窗口创建并设置分组"${newGroupName}"`)
              selectedRoles.value = []
              selectAll.value = false
            } catch (error) {
              console.error('设置分组失败:', error)
              ElMessage.error('设置分组失败: ' + (error.message || '未知错误'))
            } finally {
              batchGroupUpdating.value = false
            }
          } else {
            // 如果没有选中窗口，提示用户
            ElMessage({
              message: '没有选中窗口。使用方法：1. 先勾选窗口 2. 点击"新增分组"创建并设置；或使用"批量操作 > 设置分组"创建新分组',
              type: 'info',
              duration: 5000
            })
          }
          
          groupFilter.value = null // 清空筛选
          
          // 刷新分组列表和数据
          await Promise.all([loadUserGroups(), loadData()])
          
        } catch (error) {
          if (error !== 'cancel') {
            console.error('创建分组失败:', error)
          }
          groupFilter.value = null // 取消选择
        }
        return
      }
      
      // 普通筛选
      devicePagination.value.page = 1 // 重置到第一页
      loadData()
    }
    
    // 离线筛选处理
    const handleOfflineFilter = () => {
      devicePagination.value.page = 1 // 重置到第一页
      loadData()
    }
    
    // 排序变更处理
    const handleSortChange = () => {
      devicePagination.value.page = 1 // 重置到第一页
      loadData()
    }

    // 列排序处理
    const handleColumnSort = (sortKey) => {
      if (sortBy.value === sortKey) {
        // 如果点击的是当前排序列，切换排序顺序
        sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
      } else {
        // 如果点击的是新列，设置为该列并默认降序
        sortBy.value = sortKey
        sortOrder.value = 'desc'
      }
      devicePagination.value.page = 1 // 重置到第一页
      loadData()
    }

    // Element Plus 表格排序处理
    const handleTableSort = ({ column, prop, order }) => {
      // 根据列的 prop 找到对应的 sortKey
      let targetSortKey = null
      const columnConfig = tableColumns.value.find(col => col.key === prop)
      if (columnConfig && columnConfig.sortKey) {
        targetSortKey = columnConfig.sortKey
      } else if (prop === 'window_id') {
        targetSortKey = 'window_id'
      }
      
      if (targetSortKey) {
        // 如果点击的是当前排序列，切换排序方向
        if (sortBy.value === targetSortKey) {
          sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
        } else {
          // 如果点击的是不同列，设置为新列，默认升序
          sortBy.value = targetSortKey
          sortOrder.value = 'asc'
        }
        devicePagination.value.page = 1
        loadData()
      }
    }

    // 当前表格排序状态（用于显示排序图标）
    const currentTableSort = computed(() => {
      if (!sortBy.value) return null
      
      // 找到对应的列prop
      const columnConfig = tableColumns.value.find(col => col.sortKey === sortBy.value)
      const prop = columnConfig ? columnConfig.key : (sortBy.value === 'window_id' ? 'window_id' : null)
      
      if (!prop) return null
      
      return {
        prop: prop,
        order: sortOrder.value === 'asc' ? 'ascending' : 'descending'
      }
    })

    // Element Plus 表格选择变化处理
    const handleTableSelectionChange = (selection) => {
      selectedRoles.value = selection.map(row => row.id)
      selectAll.value = selectedRoles.value.length === filteredWindowList.value.length
    }

    // 计算表格高度
    const tableHeight = computed(() => {
      // 根据窗口高度动态计算，减去页面头部等其他元素的高度
      return 'calc(100vh - 350px)'
    })
    // 返回用户列表
    const handleBackUserList = () => {
      router.push('/dashboard/users')
    }

    onUnmounted(() => {
      stopImageAutoRefresh()
      if (relativeTimeInterval) clearInterval(relativeTimeInterval)
      
      // 清理所有MJPEG流
      canvasRefs.value.forEach((canvas) => {
        cleanupMjpegStreams(canvas)
      })
      
      // 清理钻石走势图表
      if (diamondTrendChartInstance.value) {
        diamondTrendChartInstance.value.dispose()
        diamondTrendChartInstance.value = null
      }
    })

          return {
        t,
        loading,
        viewMode,
        stats,
        userWindowLimit,
        windowList,
        filteredWindowList,
        showOfflineOnly,
        targetUserId,
        targetUsername,
        isViewMode,
        devicePagination,
        configIdFilter,
        handleDevicePageChange,
        handleDeviceSizeChange,
        handleConfigFilter,
        saveRoleName,
        saveRoleVocation,
        saveRoleAiAnswerCustom,
        getRoleVocationName,
        setRoleVocationName,
        getRoleAiAnswerCustom,
        setRoleAiAnswerCustom,
        getRoleAutoAnswerEnabled,
        handleAutoAnswerToggle,
        openAiAnswerDialog,
        handleAiAnswerDialogSave,
        handleOfflineFilter,
        sortBy,
        sortOrder,
        sortOptions,
        sortOrderOptions,
        handleSortChange,
        handleColumnSort,
        handleTableSort,
        handleTableSelectionChange,
        currentTableSort,
        tableHeight,
        computedTotalGold,
        scrollableHeader,
        scrollableBody,
        frozenBody,
        hoveredRowIndex,
        tableColumns,
        visibleScrollableColumns,
        visibleColumnsForExport,
        dynamicGridColumns,
        updateColumnVisibility,
        exportToExcel,
        getCellValue,
        syncHeaderScroll,
        syncBodyScroll,
        syncFrozenScroll,
        loadData,
        getScreenshotUrl,
        getImageKey,
        isLandscape,
        getDeviceStatus,
        getStatusText,
        getCpuUsage,
        getMemoryUsage,
        getEquipmentColorClass,
        formatDatasCount,
        getDatasCountAlertClass,
        formatRelativeTime,
        formatCombatPower,
        formatNumber,
        formatDiamond,
        formatAmber,
        formatSilver,
        formatDiamondCount,
        setCanvasRef,
        drawImageToCanvas,
        drawPlaceholder,
        drawImageOnCanvas,
        drawVideoStream,
        cleanupMjpegStreams,
        handleWindowAction,
        handleScreenshot,
        handleRestartWindow,
        handleViewLogs,
        handleViewStatus,
        handleBackUserList,
        formatStatusLabel,
        formatStatusValue,
        formatDateTime,
        handleManualRefresh,
        refreshSingleImage,
        refreshAllImages,
        updateDeviceScreenshots,
        // 日志相关
        logDialogVisible,
        logLoading,
        currentRole,
        logData,
        currentPage,
        pageSize,
        loadLogs,
        handleLogPageChange,
        handleLogSizeChange,
        // 钻石走势相关
        diamondTrendDialogVisible,
        diamondTrendLoading,
        currentDiamondTrendRole,
        diamondTrendData,
        selectedWeeks,
        diamondTrendChartRef,
        handleDiamondTrend,
        loadDiamondTrend,
        // 时间选择器
        timeRange,
        timeRangeOptions,
        selectedTimeRange,
        // 时间格式化
        formatTime,
        // 缩放级别
        zoomLevel,
        // 刷新冷却时间
        refreshCooldown,
        // 刷新倒计时
        refreshCountdown,
        // 移动端检测
        isMobile,
        // 批量选择相关
        selectedRoles,
        selectAll,
        indeterminate,
        handleSelectAll,
        handleRoleSelect,
        getSelectedRoles,
        formatSelectedWindowsText,
        // 批量配置相关
        batchConfigDialogVisible,
        selectedConfigId,
        availableConfigs,
        configsLoading,
        batchUpdating,
        showBatchConfigDialog,
        loadAvailableConfigs,
        loadConfigs,
        handleBatchConfigUpdate,
        // 批量删除相关
        batchDeleteDialogVisible,
        batchDeleting,
        showBatchDeleteDialog,
        handleBatchDelete,
        // 移动端批量操作相关
        handleMobileBatchOperation,
        // 分组相关
        groupFilter,
        userGroups,
        batchGroupDialogVisible,
        selectedGroupName,
        batchGroupUpdating,
        handleGroupFilter,
        showBatchGroupDialog,
        handleBatchGroupUpdate,
        batchAiAnswerDialogVisible,
        batchAiAnswerText,
        batchAiAnswerUpdating,
        showBatchAiAnswerDialog,
        handleBatchAiAnswerUpdate,
        handleBatchAutoAnswer,
        aiAnswerDialogVisible,
        aiAnswerEditText,
        aiAnswerDialogSaving,
        answerTestDialogVisible,
        answerTestRole,
        openAnswerTestDialog,
        handleDeleteGroup,
        // 状态查看相关
        statusDialogVisible,
        currentStatusRole,
        currentStatusList,
        // 图片放大相关
        zoomedImageUrl,
        openImageZoom,
        openCanvasZoom,
        closeImageZoom
      }
  }
}
</script>

<style scoped>
.page-content {
  padding: 0;
  height: calc(100vh - 115px); /* PC端固定高度，进一步微调 */
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  box-sizing: border-box;
  overflow: hidden; /* PC端防止页面级别滚动 */
  width: 100%;
}

.windows-management {
  height: 100%; /* 使用父容器的完整高度 */
  display: flex;
  flex-direction: column;
  overflow: hidden; /* PC端防止页面级别滚动 */
  width: 100%;
  max-width: 100vw; /* 确保不超过视窗宽度 */
}

.page-header {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow-x: hidden; /* 防止水平溢出 */
  width: 100%;
  box-sizing: border-box;
}

/* 统计数字 */
.stats-inline {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 16px;
  width: 100%;
  box-sizing: border-box;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.stat-value {
  font-size: 16px;
  font-weight: 600;
  color: #1d1d1f;
  line-height: 1;
}

.stat-label {
  font-size: 12px;
  color: #86868b;
  font-weight: 400;
  line-height: 1;
}

.stat-divider {
  width: 1px;
  height: 20px;
  background: rgba(0, 0, 0, 0.1);
}

/* 钻石统计分隔线样式 */
.diamond-divider {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  width: 2px;
  margin: 0 8px;
}

/* 钻石统计项样式 */
.diamond-stat .stat-value {
  color: #d97706;
  font-weight: 700;
}

.diamond-stat .stat-label {
  color: #92400e;
  font-weight: 600;
  line-height: 1;
}

.page-title-clickable {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  cursor: pointer;
  transition: color 0.2s ease;
  user-select: none;
}

.page-title-clickable:hover {
  color: #3b82f6;
}

/* 标题区域样式 */
.title-section {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

/* 更新日志链接样式 */
.update-log-link {
  color: #3b82f6;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: color 0.2s ease;
  white-space: nowrap;
}

.update-log-link:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

/* 查看模式徽章 */
.view-mode-badge {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.view-mode-badge .badge-icon {
  font-size: 14px;
}

.view-mode-badge .badge-text {
  white-space: nowrap;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  overflow: visible !important;
  flex-wrap: wrap;
}

/* 移动端只看离线按钮 */
.mobile-offline-filter {
  display: none;
}

/* 桌面端只看离线按钮 */
.desktop-offline-filter {
  display: block;
}

/* 移动端网格/列表切换按钮 */
.mobile-toggle-buttons {
  display: none;
}

/* 桌面端网格/列表切换按钮 */
.desktop-toggle-buttons {
  display: inline-flex;
}

/* 桌面端分页控件 */
.desktop-pagination {
  display: flex !important;
}

/* 移动端分页控件 */
.mobile-pagination {
  display: none !important;
}

/* 移动端列表操作按钮组 */
.mobile-list-actions {
  display: none;
}

/* 移动端第二行默认隐藏 */
.mobile-second-row {
  display: none;
}

.mobile-pagination-row {
  display: none;
}

/* 桌面端批量操作 */  
.desktop-batch-operations {
  display: inline-block;
}

/* 桌面端显示列按钮 */
.desktop-column-selector {
  display: block;
}

.view-controls-right {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px; /* 固定高度确保对齐 */
  position: relative;
  z-index: 1;
  overflow: visible !important;
}

.custom-pagination {
  display: flex !important;
  align-items: center !important;
  gap: 12px !important;
  font-size: 13px !important;
  height: 32px !important;
  padding: 0 8px !important;
  background: #f8f9fa !important;
  border: 1px solid #e9ecef !important;
  border-radius: 6px !important;
  box-sizing: border-box !important;
  white-space: nowrap !important;
  flex-shrink: 0 !important;
  min-width: fit-content !important;
  position: relative !important;
  z-index: 1 !important;
}

.page-size-select {
  padding: 4px 8px !important;
  border: 1px solid #d0d7de !important;
  border-radius: 4px !important;
  background: #fff !important;
  font-size: 12px !important;
  color: #24292f !important;
  cursor: pointer !important;
  outline: none !important;
  transition: border-color 0.2s !important;
  box-sizing: border-box !important;
  margin: 0 !important;
  font-family: inherit !important;
  position: relative !important;
  z-index: 1000 !important;
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
}

.page-size-select:hover {
  border-color: #0969da !important;
}

.page-size-select:focus {
  border-color: #0969da !important;
  box-shadow: 0 0 0 3px rgba(9, 105, 218, 0.1) !important;
}

.page-controls {
  display: flex !important;
  align-items: center !important;
  gap: 12px !important;
}

.page-btn {
  padding: 4px 8px !important;
  border: 1px solid #d0d7de !important;
  border-radius: 4px !important;
  background: #fff !important;
  color: #24292f !important;
  font-size: 14px !important;
  cursor: pointer !important;
  transition: all 0.2s !important;
  outline: none !important;
  box-sizing: border-box !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  text-align: center !important;
  vertical-align: middle !important;
  line-height: 1 !important;
  min-width: 24px !important;
  height: 24px !important;
  margin: 0 !important;
  text-decoration: none !important;
  font-family: inherit !important;
  font-weight: normal !important;
}

.page-btn:hover:not(:disabled) {
  background: #f3f4f6 !important;
  border-color: #8b949e !important;
}

.page-btn:active:not(:disabled) {
  background: #e5e7eb !important;
}

.page-btn:disabled {
  color: #8b949e !important;
  background: #f6f8fa !important;
  cursor: not-allowed !important;
  border-color: #d0d7de !important;
}

.page-info {
  color: #656d76 !important;
  font-size: 12px !important;
  white-space: nowrap !important;
  font-weight: 500 !important;
  margin: 0 !important;
  padding: 0 !important;
  line-height: 1.2 !important;
}


/* 确保所有控件高度统一 */
:deep(.view-controls-right .el-select) {
  height: 28px;
}

:deep(.view-controls-right .el-select .el-input__inner) {
  height: 28px;
  line-height: 28px;
}

:deep(.view-controls-right .el-checkbox) {
  height: 28px;
  line-height: 28px;
}

:deep(.el-select--small .el-select__wrapper) {
  min-height: 30px;
}

.filter-option {
  display: flex;
  align-items: center;
  height: 28px; /* 确保与其他控件对齐 */
}

/* 分组分隔选项样式 - 更紧凑 */
.group-divider-option {
  border-top: 1px dashed #e0e0e0 !important;
  margin-top: 4px !important;
  padding-top: 4px !important;
  opacity: 1 !important;
  color: #909399 !important;
  cursor: not-allowed !important;
  font-size: 12px !important;
}

.group-divider-option:hover {
  background-color: transparent !important;
}

/* 排序控件样式 */
.sort-controls {
  gap: 6px;
  align-items: center;
}

.sort-label {
  font-size: 13px;
  color: #666;
  white-space: nowrap;
}

.filter-option :deep(.el-checkbox__label) {
  font-size: 13px;
  color: #495057;
}

/* 显示列按钮样式 */
.column-selector-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 14px;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  min-width: 100px;
}

.column-selector-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.column-selector-btn:active {
  background: #f3f4f6;
  transform: translateY(0.5px);
}

.column-selector-btn svg {
  flex-shrink: 0;
  opacity: 0.7;
  transition: transform 0.2s ease;
}

/* 下拉箭头旋转动画 */
.column-selector-btn:hover svg {
  opacity: 1;
}

/* 导出Excel按钮样式 */
.export-excel-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 20px;
  background: #10b981;
  border: 1px solid #10b981;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(16, 185, 129, 0.2);
  min-width: 140px;
}

.export-excel-btn:hover {
  background: #059669;
  border-color: #059669;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
  transform: translateY(-1px);
}

.export-excel-btn:active {
  background: #047857;
  transform: translateY(0);
}

/* 列选择下拉菜单样式 */
.column-dropdown {
  min-width: 200px;
  max-height: 400px;
  overflow-y: auto;
}

.column-dropdown-header {
  padding: 12px 16px 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  border-bottom: 1px solid #e2e8f0;
}

.column-options {
  padding: 8px 0;
}

.column-option {
  padding: 6px 16px;
  display: flex;
  align-items: center;
}

.column-option:hover {
  background: #f8fafc;
}

/* 禁用状态的选项 */
.column-option :deep(.el-checkbox.is-disabled) {
  opacity: 0.5;
}

.column-option :deep(.el-checkbox.is-disabled .el-checkbox__label) {
  color: #94a3b8;
}

.refresh-btn {
  height: 32px;
  padding: 0 16px;
  background: #40c463;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.refresh-btn:hover:not(:disabled) {
  background: #3ab158;
}

.refresh-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.refresh-icon {
  width: 16px;
  height: 16px;
}

.toggle-buttons {
  display: inline-flex;
  background: #f0f2f5;
  border-radius: 4px;
  padding: 2px;
  margin-left: auto;
  width: auto;
  max-width: 200px;
}

.toggle-btn {
  padding: 0 16px;
  height: 28px;
  border: none;
  border-radius: 3px;
  font-size: 13px;
  color: #666;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex: none;
  width: auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-btn.active {
  background: white;
  color: #1d1d1f;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* 移动端适配 */
@media (max-width: 768px) {
  .page-content {
    height: 100%;
    min-height: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .windows-management {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    height: auto;
  }

  .windows-container {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .windows-grid {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    height: auto;
  }

  .windows-list-wrapper {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .windows-list-wrapper :deep(.el-table) {
    flex: 1;
    height: 100% !important;
  }

  .windows-list-wrapper :deep(.el-table__body-wrapper) {
    flex: 1;
    overflow-y: auto !important;
  }

  .windows-list-wrapper :deep(.el-table__header-wrapper th) {
    padding: 6px 4px !important;
    font-size: 12px;
  }

  .windows-list-wrapper :deep(.el-table__body td) {
    padding: 5px 4px !important;
  }

  .windows-list-wrapper :deep(.el-table__body td .cell) {
    font-size: 12px;
    line-height: 1.3;
  }

  .page-header {
    flex-shrink: 0;
    padding: 8px 10px;
    margin-bottom: 8px;
  }

  .title-section {
    margin-bottom: 6px;
  }

  .stats-inline {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 6px;
    justify-content: space-between;
    width: 100%;
    max-width: none;
  }

  .stat-item {
    flex: 1 1 calc(14.28% - 4px);
    min-width: 48px;
    flex-direction: column;
    text-align: center;
    gap: 2px;
    padding: 4px 2px;
    background: rgba(255, 255, 255, 0.6);
    border-radius: 6px;
    width: auto;
    min-height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .stat-value {
    font-size: 16px;
    font-weight: 700;
  }

  .stat-label {
    font-size: 11px;
  }

  .stat-divider {
    display: none; /* 网格布局不需要分隔线 */
  }

  .diamond-divider {
    display: none;
  }

  .header-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    width: 100%;
    align-items: center;
  }
  
  /* 第一行元素 */
  .refresh-btn {
    flex: 0 0 auto;
  }
  
  .mobile-offline-filter {
    flex: 0 0 auto;
  }
  
  .mobile-toggle-buttons {
    flex: 0 0 auto;
  }
  
  /* 第二行占满宽度 */
  .mobile-second-row {
    flex: 1 0 100%;
  }

  /* 移动端显示刷新右边的只看离线按钮 */
  .mobile-offline-filter {
    display: block;
  }

  /* 移动端隐藏原来的只看离线按钮 */
  .desktop-offline-filter {
    display: none;
  }

  /* 移动端显示网格/列表切换按钮 */
  .mobile-toggle-buttons {
    display: inline-flex;
    background: #f0f2f5;
    border-radius: 4px;
    padding: 2px;
  }

  /* 移动端隐藏桌面端的网格/列表切换按钮 */
  .desktop-toggle-buttons {
    display: none;
  }

  .mobile-toggle-btn {
    padding: 0 12px;
    height: 28px;
    border: none;
    border-radius: 3px;
    font-size: 12px;
    color: #666;
    background: transparent;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
  }

  .mobile-toggle-btn.active {
    background: white;
    color: #1d1d1f;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  /* 移动端第二行：筛选配置和批量操作 */
  .mobile-second-row {
    display: flex;
    gap: 4px;
    width: 100%;
    margin-top: 4px;
    position: relative;
    z-index: 1;
    overflow: visible !important;
    align-items: stretch;
  }

  .mobile-filter-select {
    flex: 1;
    min-width: 0;
  }
  
  .mobile-filter-select :deep(.el-select) {
    width: 100%;
    height: 32px;
  }
  
  .mobile-filter-select :deep(.el-select .el-input__wrapper) {
    height: 32px;
    border-radius: 6px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
  
  /* 移动端批量操作按钮优化 */
  .mobile-batch-btn {
    height: 32px !important;
    border-radius: 6px !important;
    font-size: 13px !important;
    flex-shrink: 0;
    min-width: 90px;
  }
  
  .mobile-second-row .custom-pagination {
    position: relative;
    z-index: 100;
    overflow: visible !important;
  }
  
  /* 移动端分页下拉菜单定位修复 */
  :deep(.mobile-pagination .el-select) {
    position: relative;
    z-index: 1000;
  }
  
  :deep(.mobile-pagination .el-select__popper) {
    z-index: 1001 !important;
  }
  
  :deep(.page-size-select-popper) {
    z-index: 1001 !important;
  }
  
  /* 移动端分页下拉菜单定位修复 */
  :deep(.mobile-pagination .el-select) {
    position: relative;
    z-index: 1000;
  }
  
  :deep(.mobile-pagination .el-select__popper) {
    z-index: 1001 !important;
    position: fixed !important;
  }
  
  :deep(.mobile-pagination .el-select__popper.el-popper[data-popper-placement^="bottom"]) {
    margin-top: 4px !important;
  }
  
  :deep(.mobile-pagination .el-select__popper.el-popper[data-popper-placement^="top"]) {
    margin-bottom: 4px !important;
  }
  
  /* 移动端分页下拉菜单全局样式 */
  :deep(.mobile-page-size-popper) {
    z-index: 1001 !important;
  }
  
  :deep(.mobile-page-size-popper.el-popper) {
    position: fixed !important;
    transform: none !important;
  }

  .mobile-filter-select :deep(.el-select) {
    width: 100%;
    height: 32px;
  }

  .mobile-filter-select :deep(.el-input) {
    height: 32px;
  }

  .mobile-filter-select :deep(.el-input__wrapper) {
    height: 32px !important;
    border-radius: 6px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    padding: 0 10px;
  }
  
  .mobile-filter-select :deep(.el-input__inner) {
    height: 32px !important;
    line-height: 32px !important;
  }

  /* 隐藏桌面端的批量操作，显示移动端的 */
  .desktop-batch-operations {
    display: none;
  }

  .mobile-batch-btn {
    flex: 1;
    padding: 0 16px;
    height: 32px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 120px;
  }

  .batch-operation-btn {
    background: linear-gradient(135deg, #007aff 0%, #0a84ff 100%) !important;
    color: white !important;
    box-shadow: 0 2px 8px rgba(0, 122, 255, 0.3);
  }

  .batch-operation-btn:active:not(.disabled) {
    transform: scale(0.98);
  }
  
  .batch-operation-btn.disabled {
    background: #e5e7eb !important;
    color: #9ca3af !important;
    box-shadow: none;
  }

  .mobile-batch-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* 隐藏移动端的批量操作按钮 */
  .mobile-list-actions {
    display: none;
  }

  /* 隐藏桌面端的显示列按钮 */
  .desktop-column-selector {
    display: none;
  }

  .refresh-btn {
    margin-bottom: 0;
    font-size: 12px;
    padding: 0 12px;
    height: 32px;
  }

  .view-toggle {
    margin-top: 0;
  }

  /* 移动端自定义分页器优化 */
  .custom-pagination {
    gap: 8px;
    padding: 0 8px;
    font-size: 11px;
    height: 28px;
  }

  .page-size-select {
    font-size: 11px;
    padding: 2px 4px;
  }

  .page-controls {
    gap: 8px;
  }

  .page-btn {
    padding: 2px 8px;
    font-size: 11px;
  }

  .page-info {
    font-size: 10px;
  }

  .jump-to {
    display: none; /* 移动端隐藏跳转功能 */
  }
}

/* 手动刷新按钮样式 */
.refresh-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #34c759, #30d158);
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.refresh-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #30d158, #32d74b);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(52, 199, 89, 0.3);
}

.refresh-btn:active:not(:disabled) {
  transform: translateY(0);
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: linear-gradient(135deg, #8e8e93, #a1a1a6) !important;
  transform: none !important;
  box-shadow: none !important;
}

.refresh-icon {
  width: 16px;
  height: 16px;
}

.refresh-icon.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}



.toggle-buttons {
  display: flex;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  padding: 2px;
  width: 100%;
}

.toggle-btn {
  flex: 1;
  background: none;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #86868b;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.toggle-btn.active {
  background: white;
  color: #1d1d1f;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* 加载状态 */
.loading-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top: 3px solid #007aff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 窗口容器 */
.windows-container {
  flex: 1;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  border-radius: 8px; /* 减小圆角 */
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden; /* PC端防止容器级别滚动 */
  margin: 0; /* 移除边距，让内容更贴近边缘 */
  min-height: 0; /* 允许flex子元素收缩 */
}

/* Grid layout with fixed 80.75% scale for 5 columns */
.windows-grid {
  --base-width: 291px; /* 306px * 0.95 = 291px, optimized for 5 columns */
  --card-width: var(--base-width);
  display: flex;
  flex-wrap: wrap;
  gap: 4px; /* 统一的间距 */
  padding: 4px; /* 减少容器内边距，从8px减少到4px */
  overflow-y: auto; /* 只允许垂直滚动 */
  overflow-x: hidden; /* 隐藏水平滚动 */
  height: 100%; /* 使用父容器的完整高度 */
  min-height: 0; /* 允许容器收缩 */
  transition: all 0.3s ease;
  align-content: flex-start; /* 从顶部开始排列 */
  /* 隐藏滚动条但保留滚动功能 */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
}

.windows-grid::-webkit-scrollbar {
  display: none; /* Chrome, Safari and Opera */
}

.window-card {
  width: var(--card-width); /* 固定宽度 */
  height: 270px; /* 桌面端也适当增加高度 */
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  flex-shrink: 0; /* 防止卡片被压缩 */
}

.window-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

/* Compact device preview */
.device-preview {
  position: relative;
  width: 100%;
  height: 150px; /* 设置固定较小高度 */
  background: #000;
  border-radius: 16px 16px 0 0;
  overflow: hidden;
  flex-shrink: 0; /* 防止被压缩 */
}

.screenshot-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.screenshot {
  width: 100%;
  height: 100%;
  display: block;
  cursor: pointer;
  transition: opacity 0.3s ease; /* 添加淡入淡出过渡效果 */
  border-radius: 8px;
}

.screenshot:hover {
  opacity: 0.9; /* 悬停时稍微降低透明度 */
}

.screenshot-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(180deg, rgba(0,0,0,0.7) 0%, transparent 50%);
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.window-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.window-id-badge {
  background: rgba(255, 255, 255, 0.95);
  color: #1d1d1f;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.role-name-badge {
  background: rgba(0, 122, 255, 0.9);
  color: white;
  padding: 3px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.role-name-cell-input {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 4px 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.4;
  color: #1d1d1f;
  background: #fff;
  font-family: inherit;
}

.role-name-cell-input:focus {
  outline: none;
  border-color: #007aff;
  box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.12);
}

.role-name-cell-input::placeholder {
  color: #c0c4cc;
}

.ai-answer-cell-preview {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  line-height: 1.4;
  color: #1d1d1f;
  cursor: pointer;
}

.ai-answer-cell-preview:hover {
  color: #007aff;
}

.answer-test-cell-link {
  display: inline-block;
  font-size: 12px;
  line-height: 1.4;
  color: #007aff;
  cursor: pointer;
  user-select: none;
  transition: color 0.15s ease;
}

.answer-test-cell-link:hover {
  color: #0056b3;
  text-decoration: underline;
}

.ai-answer-view-text {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 14px;
  line-height: 1.6;
  color: #1d1d1f;
  min-height: 120px;
  padding: 4px 0;
}

.batch-ai-answer-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.batch-ai-answer-textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid #d2d2d7;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.5;
  color: #1d1d1f;
  font-family: inherit;
  resize: vertical;
  min-height: 140px;
}

.batch-ai-answer-textarea:focus {
  outline: none;
  border-color: #007aff;
  box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.12);
}

.status-info {
  display: flex;
  align-items: flex-start;
}

/* 窗口操作按钮 */
.window-actions {
  position: absolute;
  bottom: 12px;
  right: 12px;
}

.action-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  color: #1d1d1f;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 1);
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* 齿轮图标样式 */
.gear-icon {
  width: 16px;
  height: 16px;
  color: #1d1d1f;
  transition: all 0.3s ease;
}

.action-btn:hover .gear-icon {
  transform: rotate(45deg); /* 悬停时旋转 */
  color: #007aff; /* 悬停时变蓝 */
}

/* 旧的 status-badge 样式已移除，使用下方更现代的样式定义 */

/* Ultra-compact device info card */
.device-info-card {
  padding: 6px 8px;
  background: rgba(248, 249, 250, 0.8);
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  flex: 1; /* 占满剩余空间 */
  display: flex;
  flex-direction: column;
  justify-content: flex-start; /* 从顶部开始排列，避免内容被挤压 */
  min-height: 0; /* 允许收缩 */
}

.info-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 2px; /* 进一步减少间距 */
}

.device-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.currency-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.currency-text {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  line-height: 1.2;
}

.device-name {
  font-size: 14px; /* 从16px减少到14px */
  font-weight: 600;
  color: #1d1d1f;
}

.level-badge {
  background: linear-gradient(135deg, #ff9500, #ff6b00);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
}



.update-text {
  background: linear-gradient(135deg, #34c759, #30d158);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 500;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', sans-serif;
  letter-spacing: 0.2px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  margin-left: 4px;
  opacity: 0.9;
  transition: all 0.2s ease;
}

.update-text:hover {
  opacity: 1;
  transform: translateY(-0.5px);
  box-shadow: 0 2px 4px rgba(52, 199, 89, 0.2);
}


/* Ultra-compact monitoring section */
.monitoring-section {
  margin-bottom: 2px; /* 进一步减少间距 */
}

.monitor-row {
  display: flex;
  gap: 12px; /* 从16px减少到12px */
  margin-bottom: 2px; /* 进一步减少到2px */
}

.monitor-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px; /* 进一步减少到2px */
}

.monitor-label {
  font-size: 10px; /* 从12px减少到10px */
  color: #86868b;
  font-weight: 500;
}

.mini-progress-bar {
  height: 3px; /* 从4px减少到3px */
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
  overflow: hidden;
}

.mini-progress-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.mini-progress-fill.cpu {
  background: linear-gradient(90deg, #34c759, #ff9500);
}

.mini-progress-fill.memory {
  background: linear-gradient(90deg, #007aff, #5856d6);
}

/* 新的监控布局样式 */
.cpu-memory-row {
  display: flex;
  gap: 12px;
  margin-bottom: 4px;
}

.cpu-memory-row .monitor-item {
  flex: 1;
}

.capacity-row {
  display: flex;
}

.capacity-item {
  width: 100%;
}

/* Ultra-compact details layout */
.details-compact {
  display: flex;
  flex-wrap: wrap;
  gap: 3px; /* 进一步减少间距 */
  font-size: 10px; /* 从11px减少到10px */
  align-items: center; /* 垂直居中对齐 */
  margin-top: 2px; /* 添加小的上边距 */
}

.detail-text {
  color: #86868b;
  background: rgba(255, 255, 255, 0.6);
  padding: 1px 3px; /* 进一步减少padding */
  border-radius: 3px; /* 从4px减少到3px */
  white-space: nowrap; /* 防止文字换行 */
  overflow: hidden;
  text-overflow: ellipsis;
}

.task-text {
  color: #1d1d1f;
  background: rgba(52, 199, 89, 0.1);
  padding: 1px 3px; /* 进一步减少padding */
  border-radius: 3px; /* 从4px减少到3px */
  font-weight: 500;
  white-space: nowrap; /* 防止文字换行 */
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px; /* 限制最大宽度 */
}


/* Improved list mode with larger screenshots */
.windows-list {
  height: 100%;
  overflow: hidden;
}

.windows-list-wrapper {
  flex: 1;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* Element Plus 表格样式定制 */
.windows-list-wrapper :deep(.el-table) {
  font-size: 13px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.windows-list-wrapper :deep(.el-table__body-wrapper) {
  flex: 1;
  overflow-y: auto;
}

.windows-list-wrapper :deep(.el-table__header-wrapper th) {
  background-color: #f8f9fa;
  color: #1d1d1f;
  font-weight: 600;
  font-size: 13px;
  padding: 14px 8px !important;
  height: auto;
  line-height: 1.5;
  text-align: center;
}

.windows-list-wrapper :deep(.el-table__header-wrapper th .cell) {
  padding: 0 6px;
  line-height: 1.5;
  white-space: nowrap;
  overflow: visible;
  text-align: center;
  justify-content: center;
}

.windows-list-wrapper :deep(.el-table__body td) {
  padding: 10px 8px !important;
}

.windows-list-wrapper :deep(.el-table__body td .cell) {
  padding: 0 6px;
  line-height: 1.5;
}

.windows-list-wrapper :deep(.el-table__row) {
  transition: background 0.2s ease;
}

.windows-list-wrapper :deep(.el-table__row:hover) {
  background-color: rgba(0, 122, 255, 0.05);
}

.windows-list-wrapper :deep(.el-table--striped .el-table__body tr.el-table__row--striped) {
  background-color: #fafafa;
}

.windows-list-wrapper :deep(.el-table--border) {
  border-color: rgba(0, 0, 0, 0.08);
}

.windows-list-wrapper :deep(.el-table td, .el-table th) {
  border-color: rgba(0, 0, 0, 0.05);
}

.windows-list-wrapper :deep(.el-table--border .el-table__cell) {
  border-right-color: rgba(0, 0, 0, 0.05);
}

.list-header {
  display: grid;
  grid-template-columns: 50px 60px 120px 80px 60px 100px 100px 100px 100px 120px 80px 100px minmax(150px, 1fr) 120px;
  background: #f8f9fa;
  padding: 16px 20px;
  font-weight: 600;
  color: #1d1d1f;
  font-size: 14px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  gap: 12px;
  position: sticky;
  top: 0;
  z-index: 10;
}

.list-row {
  display: grid;
  grid-template-columns: 50px 60px 120px 80px 60px 100px 100px 100px 100px 120px 80px 100px minmax(150px, 1fr) 120px;
  padding: 12px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  align-items: center;
  gap: 12px;
  transition: background 0.2s ease;
}

.checkbox-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.list-row:hover {
  background: rgba(0, 0, 0, 0.02);
}

.header-cell, .cell {
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cell {
  color: #1d1d1f;
}

/* Larger screenshots in list view */
.screenshot-cell {
  padding: 4px 0;
}

.mini-screenshot {
  width: 80px;
  height: 50px;
  border-radius: 6px;
  overflow: hidden;
  background: #f3f4f6;
}

.mini-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.3s ease; /* 添加过渡效果 */
}

/* 列表模式的迷你进度条 */
.mini-progress {
  display: flex;
  align-items: center;
  gap: 6px;
}

.list-row .mini-progress-bar {
  width: 40px;
  height: 4px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
  overflow: hidden;
}

.list-row .mini-progress-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.list-row .mini-progress-fill.cpu {
  background: linear-gradient(90deg, #34c759, #ff9500);
}

.list-row .mini-progress-fill.memory {
  background: linear-gradient(90deg, #007aff, #5856d6);
}

.mini-progress-text {
  font-size: 12px;
  color: #86868b;
  min-width: 30px;
}



.task-cell {
  max-width: 220px;
}

.update-time {
  font-size: 12px;
  color: #666;
  font-weight: 500;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', sans-serif;
  letter-spacing: 0.2px;
}

/* 空状态 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: #86868b;
  grid-column: 1 / -1;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.6;
}

.empty-text {
  font-size: 16px;
  font-weight: 500;
  color: #1d1d1f;
  margin-bottom: 8px;
}

.empty-desc {
  font-size: 14px;
  color: #86868b;
}

/* 响应式设计 */
@media (max-width: 1400px) {
  .windows-grid {
    --base-width: 258px; /* 272px * 0.95 */
  }
}

@media (max-width: 1200px) {
  .windows-grid {
    --base-width: 242px; /* 255px * 0.95 */
  }
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .view-controls {
    width: 100%;
  }
  
  .header-actions {
    flex-wrap: wrap;
    gap: 4px;
    padding: 6px;
    background: #f8f9fa;
    border-radius: 8px;
    margin-bottom: 4px;
  }
  
  /* 确保分页组件在移动端显示 */
  .mobile-pagination-row {
    display: flex !important;
    width: 100%;
    margin-top: 4px;
    justify-content: flex-end;
    position: relative;
    z-index: 1;
    overflow: visible !important;
  }
  
  /* 移动端刷新按钮优化 */
  .refresh-btn {
    height: 32px;
    padding: 0 12px;
    border-radius: 6px;
    font-size: 13px;
    flex-shrink: 0;
  }
  
  /* 移动端只看离线优化 */
  .mobile-offline-filter {
    flex-shrink: 0;
  }
  
  .mobile-offline-filter :deep(.el-checkbox) {
    height: 32px;
    display: flex;
    align-items: center;
  }
  
  /* 移动端网格/列表切换优化 */
  .mobile-toggle-buttons {
    flex-shrink: 0;
    height: 32px;
  }
  
  .mobile-toggle-btn {
    height: 32px;
    padding: 0 14px;
    border-radius: 6px;
  }

  /* 隐藏桌面端的配置筛选 */
  .view-controls-right {
    display: none;
  }
  
  /* 移动端分页控件单独一行，靠右显示 */
  .mobile-pagination-row {
    display: flex !important;
    width: 100%;
    margin-top: 4px;
    justify-content: space-between;
    align-items: center;
    position: relative;
    z-index: 1;
    overflow: visible !important;
  }
  
  /* 分页行中的只看离线 - 优化样式，与分页组件风格一致 */
  .mobile-pagination-row .mobile-offline-filter {
    display: flex;
    flex-shrink: 0;
    padding: 0;
    background: #fff;
    border-radius: 6px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    border: 1px solid #e4e7ed;
    height: 32px;
    align-items: center;
    padding-right: 10px;
  }
  
  .mobile-pagination-row .mobile-offline-filter :deep(.el-checkbox) {
    height: 32px;
    display: flex;
    align-items: center;
    margin: 0;
    padding: 0 10px;
  }
  
  .mobile-pagination-row .mobile-offline-filter :deep(.el-checkbox__label) {
    font-size: 12px;
    color: #606266;
    font-weight: 400;
    padding-left: 6px;
    line-height: 32px;
  }
  
  .mobile-pagination-row .mobile-offline-filter :deep(.el-checkbox__input) {
    margin-right: 0;
  }
  
  .mobile-pagination-row .mobile-offline-filter :deep(.el-checkbox__inner) {
    width: 16px;
    height: 16px;
    border-radius: 3px;
    border-color: #dcdfe6;
  }
  
  .mobile-pagination-row .mobile-offline-filter :deep(.el-checkbox__inner::after) {
    height: 8px;
    left: 5px;
    width: 4px;
  }
  
  .mobile-pagination-row .mobile-offline-filter :deep(.el-checkbox.is-checked .el-checkbox__inner) {
    background-color: #409eff;
    border-color: #409eff;
  }
  
  .mobile-pagination-row .mobile-pagination {
    display: flex !important;
    flex-shrink: 0;
    justify-content: flex-end;
    gap: 6px;
    height: 32px;
    align-items: center;
  }
  
  .mobile-pagination-row .mobile-pagination :deep(.el-select) {
    width: 75px !important;
    height: 32px;
  }
  
  .mobile-pagination-row .mobile-pagination :deep(.el-select .el-input__wrapper) {
    height: 32px;
    border-radius: 6px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
  
  .mobile-pagination-row .mobile-pagination .page-controls {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
  }
  
  .mobile-pagination-row .mobile-pagination .page-btn {
    width: 28px;
    height: 28px;
    padding: 0;
    border-radius: 6px;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .mobile-pagination-row .mobile-pagination .page-info {
    font-size: 12px;
    padding: 0 4px;
    min-width: 36px;
    text-align: center;
  }
  
  /* 桌面端隐藏移动端分页 */
  .desktop-pagination {
    display: none !important;
  }

  .windows-grid {
    padding: 4px; /* 减少移动端边距，与桌面端保持一致 */
    gap: 4px; /* 减少移动端卡片间距，与桌面端保持一致 */
  }
  
  .window-card {
    width: calc(50% - 2px) !important; /* 移动端每行2个，减去间距的一半 */
    height: 280px !important; /* 移动端进一步增加高度，提供更宽松的显示空间 */
    flex-shrink: 0; /* 防止卡片被压缩 */
  }
  
  .device-preview {
    height: 150px !important; /* 移动端图片进一步增加高度，适配更高的卡片 */
  }
  
  /* 移动端字体和间距调整 */
  .device-name {
    font-size: 12px !important;
    max-width: 4em; /* 限制最多4个字符宽度 */
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
  }
  
  .level-badge {
    font-size: 8px !important;
    padding: 1px 4px !important;
  }
  
  .update-text {
    font-size: 7px !important;
    padding: 1px 3px !important;
    margin-left: 2px !important;
  }
  
  
  .monitor-label {
    font-size: 9px !important;
  }
  
  .detail-text, .task-text {
    font-size: 9px !important;
  }
  
  /* 移动端进度条调整 */
  .mini-progress-bar {
    height: 3px !important;
  }
  
  /* 移动端信息区域调整 */
  .device-info-card {
    padding: 8px !important; /* 增加内边距，让内容更宽松 */
  }
  
  .monitoring-section {
    margin: 6px 0 !important; /* 增加监控区域的上下间距 */
  }
  
  .monitor-row {
    gap: 8px !important;
  }
  
  /* CPU和内存行样式 */
  .cpu-memory-row {
    display: flex;
    gap: 8px;
    margin-bottom: 4px;
  }
  
  .cpu-memory-row .monitor-item {
    flex: 1;
  }
  
  /* 背包行样式 */
  .capacity-row {
    display: flex;
  }
  
  .capacity-item {
    width: 100%;
  }
  
  /* CPU和内存监控在小屏幕下正常显示 */
  
  /* 表格视图中角色名称省略 */
  .scrollable-body-content .cell {
    max-width: 80px; /* 限制单元格最大宽度 */
  }
  
  .details-compact {
    gap: 4px !important;
  }

  /* 移动端按钮样式调整 */
  .header-actions {
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: 6px;
    flex-wrap: nowrap;
    width: 100%;
  }

  .refresh-btn {
    flex: none;
    margin-bottom: 0;
    padding: 0 12px;
    height: 28px;
    font-size: 12px;
  }

  .toggle-buttons {
    flex: none;
    margin-left: 6px;
    background: #f0f2f5;
    border-radius: 4px;
    padding: 2px;
    display: inline-flex;
    width: auto;
    max-width: 280px;
  }

  .toggle-btn {
    padding: 0 18px;
    height: 28px;
    font-size: 13px;
    min-width: 0;
    max-width: 130px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

/* 超窄屏幕优化 */
@media (max-width: 480px) {
  .page-header {
    padding: 6px 8px;
    margin-bottom: 6px;
  }

  .title-section {
    margin-bottom: 4px;
  }

  .update-log-link {
    font-size: 12px;
  }

  .stats-inline {
    gap: 3px;
    margin-bottom: 4px;
  }

  .stat-item {
    flex: 1 1 calc(14.28% - 3px);
    min-width: 44px;
    gap: 1px;
    padding: 3px 1px;
    min-height: 34px;
  }

  .stat-value {
    font-size: 13px;
    font-weight: 700;
  }

  .stat-label {
    font-size: 9px;
  }

  .header-actions {
    gap: 6px;
    flex-wrap: wrap;
    justify-content: space-between;
    width: 100%;
  }

  /* 移动端显示刷新右边的只看离线按钮 */
  .mobile-offline-filter {
    display: block;
    flex: 0 0 auto;
  }

  /* 移动端隐藏原来的只看离线按钮 */
  .desktop-offline-filter {
    display: none;
  }

  /* 移动端显示网格/列表切换按钮 */
  .mobile-toggle-buttons {
    display: inline-flex;
    background: #f0f2f5;
    border-radius: 4px;
    padding: 2px;
    flex: 0 0 auto;
  }

  /* 移动端隐藏桌面端的网格/列表切换按钮 */
  .desktop-toggle-buttons {
    display: none;
  }

  .mobile-toggle-btn {
    padding: 0 10px;
    height: 26px;
    border: none;
    border-radius: 3px;
    font-size: 11px;
    color: #666;
    background: transparent;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
  }

  .mobile-toggle-btn.active {
    background: white;
    color: #1d1d1f;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  /* 移动端第二行显示 */
  .mobile-second-row {
    display: flex !important;
  }

  /* 隐藏桌面端的批量操作 */
  .desktop-batch-operations {
    display: none;
  }

  /* 隐藏桌面端的显示列按钮 */
  .desktop-column-selector {
    display: none;
  }

  .refresh-btn {
    font-size: 12px;
    padding: 0 10px;
    height: 30px;
    flex: 0 0 auto;
  }

  .toggle-buttons {
    flex: 1;
    margin-left: 6px;
    min-width: 0;
  }

  .toggle-btn {
    padding: 0 10px;
    height: 30px;
    font-size: 12px;
  }

  .custom-pagination {
    gap: 6px;
    padding: 0 8px;
    font-size: 11px;
    height: 30px;
    flex-wrap: wrap;
    justify-content: center;
  }

  .page-size-select {
    font-size: 11px;
    padding: 2px 4px;
  }

  .page-btn {
    padding: 2px 6px;
    font-size: 11px;
  }

  .page-info {
    font-size: 10px;
  }
  
  /* CPU和内存监控在超小屏幕下也正常显示 */
  
  /* 超小屏幕下角色名称更严格的省略 */
  .device-name {
    max-width: 3.5em !important; /* 超小屏幕限制为3.5个字符 */
  }
  
  .scrollable-body-content .cell {
    max-width: 60px !important; /* 超小屏幕表格单元格更窄 */
  }
  
  /* 超小屏幕下窗口卡片高度调整 */
  .window-card {
    height: 300px !important; /* 超小屏幕大幅增加高度，提供充足空间 */
  }
  
  .device-preview {
    height: 160px !important; /* 超小屏幕图片高度相应调整 */
  }
  
  /* 超小屏幕监控布局优化 */
  .cpu-memory-row {
    margin-bottom: 3px !important;
  }
  
  .cpu-memory-row .monitor-item {
    flex: 1;
  }
  
  .capacity-item {
    width: 100%;
  }
}

/*  日志模态窗口悬浮样式改进 */
:deep(.log-dialog-floating) {
  .el-dialog {
    margin-top: 5vh !important;
    border-radius: 12px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
  
  .el-dialog__header {
    padding: 16px 20px 12px;
    border-bottom: 1px solid #f0f0f0;
  }
  
  .el-dialog__title {
    font-size: 14px;
    font-weight: 600;
    color: #2c3e50;
  }
  
  .el-dialog__body {
    padding: 16px 20px;
    max-height: 70vh;
    overflow: hidden;
  }
  
  .el-dialog__footer {
    padding: 12px 20px 16px;
    border-top: 1px solid #f0f0f0;
  }
}

/* 移动端日志弹窗优化 */
@media (max-width: 768px) {
  :deep(.log-dialog-floating) {
    .el-dialog {
      margin: 2vh auto !important;
      border-radius: 8px;
      max-width: 95vw !important;
    }
    
    .el-dialog__header {
      padding: 12px 16px 8px;
    }
    
    .el-dialog__title {
      font-size: 13px;
    }
    
    .el-dialog__body {
      padding: 12px 16px;
      max-height: 60vh;
    }
    
    .el-dialog__footer {
      padding: 8px 16px 12px;
    }
  }
}

/*  美化日志头部样式 */
.log-header {
  margin-bottom: 16px;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.log-info .log-badges {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
  width: 100%;
}

.log-window-badge,
.log-role-badge,
.log-device-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
  flex-shrink: 0;
}

.log-window-badge {
  background: #4a90e2;
  color: white;
}

.log-role-badge {
  background: #7ed321;
  color: white;
}

.log-device-badge {
  background: #f5a623;
  color: white;
}

.log-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.time-range-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  max-width: 200px;
}

.time-label {
  font-size: 11px;
  color: #666;
  white-space: nowrap;
  flex-shrink: 0;
}

/* 强制时间选择器下拉框的宽度 */
:deep(.time-range-selector .el-select) {
  width: 100% !important;
  max-width: 140px !important;
}

:deep(.time-range-selector .el-select .el-input__wrapper) {
  width: 100% !important;
  max-width: 140px !important;
}

.log-stats {
  flex-shrink: 0;
  min-width: 0;
}

.log-stats .log-count {
  font-size: 11px;
  color: #888;
  background: #f8f9fa;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: 160px;
}

/*  日志列表样式 - 缩小字体、日期和文字同行 */
.log-list-container {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #fafafa;
}

.log-list {
  padding: 8px;
}

.log-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 6px 8px;
  margin-bottom: 4px;
  background: white;
  border-radius: 6px;
  border-left: 3px solid #e3f2fd;
  font-size: 11px;
  line-height: 1.4;
}

.log-item:hover {
  background: #f8f9ff;
  border-left-color: #2196f3;
}

.log-time {
  flex-shrink: 0;
  color: #666;
  font-family: 'Courier New', monospace;
  font-size: 10px;
  font-weight: 500;
  background: #f0f0f0;
  padding: 2px 6px;
  border-radius: 4px;
  min-width: 60px;
  text-align: center;
}

.log-content {
  flex: 1;
  color: #333;
  word-break: break-all;
  font-size: 11px;
}

/*  分页器样式调整 */
.log-pagination {
  margin-top: 12px;
  display: flex;
  justify-content: center;
}

:deep(.log-pagination .el-pagination) {
  font-size: 11px;
}

:deep(.log-pagination .el-pagination .el-pager li) {
  font-size: 11px;
  min-width: 24px;
  height: 24px;
  line-height: 24px;
}

/* 移动端分页器优化 */
@media (max-width: 768px) {
  :deep(.log-pagination .el-pagination) {
    font-size: 10px;
  }
  
  :deep(.log-pagination .el-pagination .el-pager li) {
    font-size: 10px;
    min-width: 20px;
    height: 20px;
    line-height: 20px;
  }
  
  :deep(.log-pagination .el-pagination .el-pagination__sizes) {
    display: none; /* 移动端隐藏每页条数选择器 */
  }
}

/* 加载和空状态样式调整 */
.log-loading,
.log-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #999;
}

.log-loading .spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #409eff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.log-loading p,
.log-empty .empty-text {
  margin: 8px 0 0 0;
  font-size: 12px;
}

.log-empty .empty-icon {
  font-size: 24px;
  margin-bottom: 8px;
}

/* 对话框底部按钮样式 */
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

:deep(.dialog-footer .el-button) {
  font-size: 8px;
  border-radius: 4px;
  padding: 6px 12px;
}

/* 批量操作按钮样式 */
.batch-config-btn {
  height: 32px;
  padding: 0 16px;
  background: #007aff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 桌面端批量操作按钮样式 */
.desktop-batch-operation-btn {
  background: #f8f9fa !important;
  border: 1px solid #007aff !important;
  color: #007aff !important;
  font-weight: 500;
}

.desktop-batch-operation-btn:hover:not(.disabled) {
  background: #007aff !important;
  color: white !important;
}

/* 批量操作下拉菜单样式 */
:deep(.batch-operations-dropdown) {
  min-width: 130px !important;
}

:deep(.batch-operations-dropdown .el-dropdown-menu) {
  min-width: 130px !important;
}

:deep(.batch-operations-dropdown .el-dropdown-menu__item) {
  min-width: 130px !important;
  width: 130px !important;
  text-align: center !important;
  justify-content: center !important;
  display: flex !important;
  align-items: center !important;
  padding: 10px 16px !important;
  font-size: 13px !important;
  white-space: nowrap !important;
  box-sizing: border-box !important;
}

:deep(.batch-operations-dropdown .el-dropdown-menu__item i),
:deep(.batch-operations-dropdown .el-dropdown-menu__item .el-icon) {
  margin-right: 6px !important;
  font-size: 14px !important;
  flex-shrink: 0 !important;
}

/* PC端专用样式 */
.desktop-batch-operations :deep(.el-dropdown-menu) {
  min-width: 130px !important;
}

.desktop-batch-operations :deep(.el-dropdown-menu__item) {
  min-width: 130px !important;
  width: 130px !important;
  text-align: center !important;
  justify-content: center !important;
  display: flex !important;
  align-items: center !important;
  padding: 10px 16px !important;
  font-size: 13px !important;
  white-space: nowrap !important;
  box-sizing: border-box !important;
}

/* 全局批量操作下拉菜单样式强制应用 */
.el-dropdown-menu.batch-operations-dropdown {
  min-width: 130px !important;
}

.el-dropdown-menu.batch-operations-dropdown .el-dropdown-menu__item {
  min-width: 130px !important;
  width: 130px !important;
  text-align: center !important;
  justify-content: center !important;
  display: flex !important;
  align-items: center !important;
  padding: 10px 16px !important;
  font-size: 13px !important;
  white-space: nowrap !important;
  box-sizing: border-box !important;
}

.batch-config-btn:hover {
  background: #0056cc;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 122, 255, 0.3);
}

.batch-config-btn:active {
  transform: translateY(0);
}

.batch-config-btn:disabled,
.batch-config-btn.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #d1d5db;
  color: #9ca3af;
}

.batch-config-btn:disabled:hover,
.batch-config-btn.disabled:hover {
  background: #d1d5db;
  transform: none;
  box-shadow: none;
}

/* 批量删除按钮样式 */
.batch-delete-btn {
  height: 32px;
  padding: 0 16px;
  background: #ff4757;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  margin-left: 8px;
}

.batch-delete-btn:hover {
  background: #ff3742;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(255, 71, 87, 0.3);
}

.batch-delete-btn:active {
  transform: translateY(0);
}

.batch-delete-btn:disabled,
.batch-delete-btn.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #d1d5db;
  color: #9ca3af;
}

.batch-delete-btn:disabled:hover,
.batch-delete-btn.disabled:hover {
  background: #d1d5db;
  transform: none;
  box-shadow: none;
}

/* 批量配置对话框样式 */
.batch-config-content {
  padding: 8px 0;
}

.selected-info {
  margin-bottom: 20px;
}

.selected-info p {
  margin: 0 0 12px 0;
  color: #1d1d1f;
  font-size: 14px;
}

.selected-windows {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 120px;
  overflow-y: auto;
}

.window-tag {
  background: #f0f2f5;
  color: #666;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  border: 1px solid #d9d9d9;
}

.config-selector {
  margin-top: 16px;
}

.selector-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #1d1d1f;
}

/* 批量删除对话框样式 */
.batch-delete-content {
  padding: 8px 0;
}

.warning-info {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 24px;
  padding: 16px;
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
}

.warning-icon {
  font-size: 24px;
  line-height: 1;
  margin-top: 2px;
}

.warning-text p {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #333;
}

.warning-text p:last-child {
  margin-bottom: 0;
}

.danger-text {
  color: #ff4757;
  font-weight: 600;
}

.warning-note {
  color: #8b4513;
  font-size: 13px;
}

.selected-windows-preview {
  margin-top: 16px;
}

.preview-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.delete-windows-preview {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 12px;
  max-height: 120px;
  overflow-y: auto;
  font-size: 13px;
  color: #666;
  line-height: 1.5;
}


@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 批量配置对话框样式优化 */
:deep(.batch-config-dialog) {
  .el-dialog {
    border-radius: 16px;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(20px);
    background: rgba(255, 255, 255, 0.95);
  }
  
  .el-dialog__header {
    padding: 24px 24px 16px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    background: #ffffff;
    border-radius: 16px 16px 0 0;
  }
  
  .el-dialog__title {
    font-size: 18px;
    font-weight: 600;
    color: #1a1a1a;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .el-dialog__title::before {
    content: "⚙️";
    font-size: 20px;
  }
  
  .el-dialog__body {
    padding: 24px;
    background: #ffffff;
  }
  
  .el-dialog__footer {
    padding: 16px 24px 24px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    background: #fafbfc;
    border-radius: 0 0 16px 16px;
  }
}

/* Enhanced batch config content styling */
.batch-config-content {
  padding: 0;
}

.selected-info {
  margin-bottom: 24px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  position: relative;
}

.selected-info p {
  margin: 0 0 16px 0;
  color: #1a1a1a;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.selected-info p::before {
  content: "📋";
  font-size: 18px;
}

.selected-info strong {
  color: #2196f3;
  background: rgba(33, 150, 243, 0.1);
  padding: 2px 8px;
  border-radius: 6px;
  font-weight: 700;
}

.selected-windows {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 140px;
  overflow-y: auto;
  padding: 4px;
}

.selected-windows::-webkit-scrollbar {
  width: 6px;
}

.selected-windows::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 3px;
}

.selected-windows::-webkit-scrollbar-thumb {
  background: rgba(33, 150, 243, 0.3);
  border-radius: 3px;
}

.selected-windows::-webkit-scrollbar-thumb:hover {
  background: rgba(33, 150, 243, 0.5);
}

.window-tag {
  background: linear-gradient(135deg, #ffffff, #f8f9fa);
  color: #2196f3;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  border: 2px solid rgba(33, 150, 243, 0.2);
  box-shadow: 0 2px 4px rgba(33, 150, 243, 0.1);
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
}

.window-tag::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

.window-tag:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2);
  border-color: rgba(33, 150, 243, 0.4);
}

.window-tag:hover::before {
  left: 100%;
}

.selected-windows-text {
  background: #f8f9fa;
  color: #495057;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  border: 1px solid #e9ecef;
  margin-top: 8px;
  line-height: 1.5;
}

.config-selector {
  margin-top: 24px;
  padding: 24px;
  /* Simplified background to clean white with subtle border */
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.selector-label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 17px;
  font-weight: 600;
  /* Changed to clean black text like Apple */
  color: #1d1d1f;
  letter-spacing: -0.022em;
}

.selector-label::before {
  /* Removed emoji, using clean design */
  content: "";
}

:deep(.config-selector .el-select) {
  width: 100%;
}

:deep(.config-selector .el-select .el-input__wrapper) {
  border-radius: 12px;
  /* Clean border styling like Apple */
  border: 1px solid rgba(0, 0, 0, 0.12);
  transition: all 0.2s ease;
  background: #fbfbfd;
  min-height: 44px;
}

:deep(.config-selector .el-select .el-input__wrapper:hover) {
  /* Subtle blue accent like Apple */
  border-color: #007aff;
  background: #ffffff;
}

:deep(.config-selector .el-select .el-input__wrapper.is-focus) {
  /* Apple-style focus state */
  border-color: #007aff;
  box-shadow: 0 0 0 4px rgba(0, 122, 255, 0.1);
  background: #ffffff;
}

.config-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 6px 0;
  line-height: 1.4;
}

.config-name {
  font-weight: 500;
  color: #1d1d1f;
  font-size: 14px;
  letter-spacing: -0.016em;
  flex: 1;
  text-align: left;
}

.config-game {
  font-size: 12px;
  color: #86868b;
  background: #f5f5f7;
  padding: 2px 8px;
  border-radius: 6px;
  border: none;
  margin-left: 8px;
  white-space: nowrap;
  font-weight: 400;
}

/* Enhanced dialog footer buttons */
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

:deep(.dialog-footer .el-button) {
  font-size: 14px;
  border-radius: 8px;
  padding: 10px 20px;
  font-weight: 600;
  transition: all 0.2s ease;
}

:deep(.dialog-footer .el-button--default) {
  background: #f5f5f5;
  border: 1px solid #d9d9d9;
  color: #666;
}

:deep(.dialog-footer .el-button--default:hover) {
  background: #e8e8e8;
  border-color: #bfbfbf;
  transform: translateY(-1px);
}

:deep(.dialog-footer .el-button--primary) {
  background: #3b82f6;
  border: 1px solid #3b82f6;
  color: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

:deep(.dialog-footer .el-button--primary:hover) {
  background: #2563eb;
  border-color: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

/* Enhanced list mode styling with proper synchronization */
.windows-list {
  height: 100%;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* 表格主体区域 */
.table-body {
  flex: 1;
  display: flex;
  overflow: hidden;
  min-height: 0;
}

/* 表头区域 */
.table-header {
  display: flex;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: #ffffff;
  position: sticky;
  top: 0;
  z-index: 10;
}

.frozen-header-section {
  display: grid;
  grid-template-columns: 50px 60px;
  width: 110px;
  flex-shrink: 0;
  height: 60px; /* 固定表头高度 */
  background: #ffffff;
  border-right: 2px solid rgba(0, 0, 0, 0.08);
  align-items: center;
}

.scrollable-header-section {
  flex: 1;
  overflow-x: auto; /* 恢复水平滚动 */
  overflow-y: hidden; /* 隐藏垂直滚动 */
  height: 60px; /* 与冻结表头相同高度 */
  /* 隐藏表头滚动条 */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
}

.scrollable-header-section::-webkit-scrollbar {
  display: none; /* Chrome, Safari and Opera - 隐藏表头滚动条 */
}

.scrollable-header-content {
  display: grid;
  height: 60px; /* 确保内容高度一致 */
  gap: 12px;
  align-items: center;
  padding: 0 20px; /* 统一内边距 */
  min-width: 1600px; /* 恢复最小宽度以支持水平滚动 */
  width: 100%;
  box-sizing: border-box;
}


.frozen-body-section {
  width: 110px;
  flex-shrink: 0;
  border-right: 2px solid rgba(0, 0, 0, 0.08);
  background: #ffffff;
  overflow-x: auto; /* 只允许水平滚动 */
  overflow-y: hidden; /* 隐藏垂直滚动 */
  scrollbar-width: thin; /* Firefox 显示细滚动条 */
  scrollbar-color: #c1c1c1 #f1f1f1; /* Firefox 滚动条颜色 */
}

.frozen-body-section::-webkit-scrollbar {
  height: 8px; /* 水平滚动条高度 */
  width: 0; /* 隐藏垂直滚动条 */
}

.frozen-body-section::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.frozen-body-section::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.frozen-body-section::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.scrollable-body-section {
  flex: 1;
  overflow: auto; /* 允许水平和垂直滚动 */
  min-height: 0;
  scrollbar-width: thin; /* Firefox 显示细滚动条 */
  scrollbar-color: #c1c1c1 #f1f1f1; /* Firefox 滚动条颜色 */
}

.scrollable-body-section::-webkit-scrollbar {
  width: 8px; /* 垂直滚动条宽度 */
  height: 8px; /* 水平滚动条高度 */
}

.scrollable-body-section::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.scrollable-body-section::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.scrollable-body-section::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.scrollable-body-section::-webkit-scrollbar-corner {
  background: #f1f1f1;
}

.scrollable-body-content {
  min-width: 1600px; /* 恢复最小宽度以支持水平滚动 */
  width: 100%;
}

/* 统一的行样式 */
.table-row {
  display: grid;
  height: 56px; /* 固定行高 */
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
  background: #ffffff;
  width: 100%;
  box-sizing: border-box;
}

/* 冻结列的行 */
.frozen-body-section .table-row {
  grid-template-columns: 50px 60px;
  padding: 0;
}

/* 滚动列的行 */
.scrollable-body-content .table-row {
  padding: 0 20px;
  gap: 12px;
  width: 100%;
  min-width: 1600px; /* 恢复最小宽度以支持水平滚动 */
  box-sizing: border-box;
}

.table-row.row-hover {
  background: linear-gradient(135deg, #f8f9ff 0%, #fff5f8 100%) !important;
  width: 100% !important;
  position: relative !important;
}

.table-row:nth-child(even) {
  background: #fafbfc;
}

/* 确保悬停效果优先级更高 */
.table-row:nth-child(even).row-hover {
  background: linear-gradient(135deg, #f8f9ff 0%, #fff5f8 100%) !important;
  width: 100% !important;
  position: relative !important;
}

.header-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-weight: 600;
  letter-spacing: -0.008em;
  /* Apple-style secondary text color for headers */
  color: #86868b;
  text-transform: uppercase;
  font-size: 13px; /* 从11px增加到13px */
}

.header-cell.sortable {
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
}

.header-cell.sortable:hover {
  color: #007aff;
  background: rgba(0, 122, 255, 0.05);
}

.header-cell .sort-icon {
  font-size: 12px;
  color: #007aff;
  margin-left: 4px;
  font-weight: bold;
  opacity: 0.6;
  transition: all 0.2s ease;
}

.header-cell.sortable:hover .sort-icon {
  color: #007aff;
  opacity: 1;
  transform: scale(1.2);
}

.checkbox-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.cell {
  color: #2c3e50;
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
}

.world-exchange-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
  white-space: normal;
}

.settlement-info {
  font-size: 12px;
  color: #10b981;
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 500;
  white-space: nowrap;
}

.status-badge {
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.online {
  background: #4caf50;
  color: white;
}

.status-badge.warning {
  background: #ff9800;
  color: white;
}

.status-badge.offline {
  background: #f44336;
  color: white;
}

.update-time {
  font-size: 12px;
  color: #7b68ee;
  font-weight: 600;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', sans-serif;
  letter-spacing: 0.3px;
  background: rgba(123, 104, 238, 0.1);
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid rgba(123, 104, 238, 0.2);
}

.task-cell {
  max-width: 220px;
  font-weight: 600;
  color: #5e72e4;
  background: rgba(94, 114, 228, 0.1);
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid rgba(94, 114, 228, 0.2);
}

/* Enhanced batch operation button - White style */
.batch-config-btn {
  height: 36px;
  padding: 0 20px;
  background: #ffffff;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  letter-spacing: 0.3px;
}

.batch-config-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.batch-config-btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.batch-config-btn:disabled,
.batch-config-btn.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f3f4f6;
  color: #9ca3af;
  border-color: #e5e7eb;
  box-shadow: none;
}

.batch-config-btn:disabled:hover,
.batch-config-btn.disabled:hover {
  background: #f3f4f6;
  transform: none;
  box-shadow: none;
}

/* Enhanced empty state for list mode - removed for cleaner UI */

.windows-list .empty-icon {
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.7;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

.windows-list .empty-text {
  font-size: 18px;
  font-weight: 600;
  color: #667eea;
  margin-bottom: 8px;
}

.windows-list .empty-desc {
  font-size: 14px;
  color: #86868b;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 装备列表样式 */
.equipment-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  font-size: 11px;
}

.equipment-item {
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: 500;
  white-space: nowrap;
}

/* 简化装备列表样式 */
.equipment-simple-list {
  display: flex;
  gap: 2px;
  font-size: 11px;
  justify-content: center;
  align-items: center;
}

.equipment-simple-item {
  padding: 1px 4px;
  border-radius: 2px;
  font-weight: 600;
  min-width: 16px;
  text-align: center;
  font-size: 10px;
}

/* 钻石走势对话框样式 */
.diamond-trend-content {
  padding: 16px 0;
}

.trend-controls {
  margin-bottom: 16px;
  display: flex;
  justify-content: flex-end;
}

.trend-loading {
  padding: 20px;
}

.trend-chart-container {
  width: 100%;
  height: 400px;
}

.trend-chart {
  width: 100%;
  height: 100%;
}

.trend-no-data {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.trend-no-data p {
  margin: 0;
  font-size: 14px;
}

/* 移动端钻石走势对话框优化 */
@media (max-width: 768px) {
  .trend-chart-container {
    height: 300px;
  }
}

.equipment-gray {
  background: #f5f5f5;
  color: #666;
  border: 1px solid #ddd;
}

.equipment-blue {
  background: #e3f2fd;
  color: #1976d2;
  border: 1px solid #90caf9;
}

.equipment-green {
  background: #e8f5e8;
  color: #388e3c;
  border: 1px solid #81c784;
}

.equipment-red {
  background: #ffebee;
  color: #d32f2f;
  border: 1px solid #ef9a9a;
}

.equipment-gold {
  background: #fff8e1;
  color: #f57c00;
  border: 1px solid #ffcc02;
}

/* 待领取样式 */
.pending-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 11px;
  justify-content: center;
  align-items: center;
  min-height: 20px;
}

.pending-item {
  padding: 1px 5px;
  border-radius: 3px;
  font-weight: 600;
  white-space: nowrap;
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #f87171;
  line-height: 1.2;
  font-size: 10px;
}

.pending-partner {
  background: #fecaca;
  color: #7f1d1d;
  border: 1px solid #f87171;
}

.pending-pet {
  background: #fca5a5;
  color: #7f1d1d;
  border: 1px solid #ef4444;
}

.pending-valkyrie {
  background: #f87171;
  color: #ffffff;
  border: 1px solid #dc2626;
}

.pending-empty {
  color: #94a3b8;
  font-size: 12px;
}

/* 经验列：与表格默认字色一致，无背景/警示色 */
.exp-cell-plain {
  color: inherit;
  font-weight: normal;
}

.count-alert-hp {
  color: #ef4444;
  font-weight: 600;
}

.count-alert-mp {
  color: #2563eb;
  font-weight: 600;
}

.count-alert-pet {
  color: #ef4444;
  font-weight: 600;
}

/* 容量显示样式 */
.capacity-display {
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 3px;
}

.capacity-warning {
  color: #dc3545 !important;
  background: #fff5f5;
}

/* 容量/经验进度条样式 - 颜色由 getExpProgressBarStyle 等函数动态设置 */

/* 状态对话框样式 */
.status-content {
  max-height: 400px;
  overflow-y: auto;
}

.no-status {
  text-align: center;
  color: #999;
  padding: 40px 0;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
  padding: 8px 0;
}

.status-card {
  display: flex;
  flex-direction: column;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
}

.status-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-color: #007aff;
}

.status-card-title {
  font-weight: 600;
  color: #333;
  font-size: 14px;
  margin-bottom: 8px;
  line-height: 1.4;
}

.status-card-time {
  color: #666;
  font-size: 12px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
  word-break: break-all;
}

/* 图片放大弹窗 */
.image-zoom-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  cursor: pointer;
}

.zoomed-image {
  max-width: 90vw;
  max-height: 90vh;
  transform: scale(0.9);
  cursor: default;
  border-radius: 8px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
}

</style>

<!-- 全局样式，确保批量操作下拉菜单在PC端正确显示 -->
<style>
.el-dropdown-menu.batch-operations-dropdown {
  min-width: 130px !important;
}

.el-dropdown-menu.batch-operations-dropdown .el-dropdown-menu__item {
  min-width: 130px !important;
  width: 130px !important;
  text-align: center !important;
  justify-content: center !important;
  display: flex !important;
  align-items: center !important;
  padding: 10px 16px !important;
  font-size: 13px !important;
  white-space: nowrap !important;
  box-sizing: border-box !important;
}

.el-dropdown-menu.batch-operations-dropdown .el-dropdown-menu__item i,
.el-dropdown-menu.batch-operations-dropdown .el-dropdown-menu__item .el-icon {
  margin-right: 6px !important;
  font-size: 14px !important;
  flex-shrink: 0 !important;
}

/* 额外的强制样式，直接针对桌面端批量操作下拉菜单 */
.desktop-batch-operations .el-dropdown-menu {
  min-width: 130px !important;
}

.desktop-batch-operations .el-dropdown-menu .el-dropdown-menu__item {
  min-width: 130px !important;
  width: 130px !important;
  text-align: center !important;
  justify-content: center !important;
  display: flex !important;
  align-items: center !important;
  padding: 10px 16px !important;
  font-size: 13px !important;
  white-space: nowrap !important;
  box-sizing: border-box !important;
}

/* 嵌套下拉菜单样式 */
.el-dropdown-menu.batch-operations-dropdown .el-dropdown-menu__item.nested-dropdown-item {
  padding: 0 !important;
  width: 100% !important;
  min-width: 150px !important; /* 稍微加宽以容纳箭头 */
  display: block !important;
}

.nested-trigger {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 16px;
  box-sizing: border-box;
  cursor: pointer;
  font-size: 13px;
  position: relative;
}

.nested-trigger span {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* 确保嵌套的下拉菜单宽度也合适 */
.nested-dropdown .el-dropdown-menu {
  min-width: 130px;
}

.nested-trigger .el-icon {
  margin-right: 6px;
  font-size: 16px;
}

.nested-trigger .submenu-caret {
  margin-left: auto;
  margin-right: 0;
  color: #909399;
}
</style>
